import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { startTranscriptionSession } from '../lib/recorder/TranscriptionSessionUtils';
import { acquireRecordingStream } from '../lib/recorder/acquireRecordingStream';
import type { AudioMixerResult } from '../lib/recorder/acquireRecordingStream';
import { useAccessToken, useTemplatesList, useIsAuthenticated } from '@features/user';
import { startNoteProcessingSession } from '../lib/recorder/NoteProcessingSessionUtils';
import { useLocalStorageState } from '../hooks/useLocalStorageState';
import { useAudioDataContext } from './AudioDataContext';
import APIService, { FRONTEND_RECORDING_APP_VERSION, API_BASE_URL } from '../services/APIService';
import { NoteInformation, RecordingStatus } from '@shared/Types/RecordingTypes';
import { useUIState } from './uiState';
import { AudioHealthSummary, RecordingSession } from '../lib/recorder/RecordingSession';
import SentryService from '../services/SentryService';
import { useSnackbar } from 'notistack';
import tabCommunicator from '../lib/tabCommunicator';
import { StorageCleanupDialog } from '../components/StorageCleanupDialog';
import { Button } from '@mui/material';
import {
  AudioInputDeviceService,
  MicLevelMeter,
  normalizedToMediaDeviceInfo,
} from '@services/audio';
import type {
  AudioConstraints,
  HealthState,
  DeviceDiff,
  NormalizedDevice,
  PermissionResult as AudioPermissionResult,
} from '@services/audio';
import { useEventTracking } from '@services/statsig';
import { EventNames } from '@services/statsig/constants';
import { getStatsigClientInstance } from '@services/statsig/trackNoteTelemetryToStatsig';
import { StorageService, storageErrorTags } from '@services/storage';
import { UploadService } from '@services/upload';
import { RecordingService, DEFAULT_TIMESLICE_MS } from '@services/recording';
import type { RecordingEventMap } from '@services/recording';
import {
  SessionCompletionService,
  SessionManifest,
  SessionRecoveryService,
} from '@services/session';
import { DailyCallService, DeviceBridgeService } from '@services/dailycall';
import type { DailyCallEventMap } from '@services/dailycall';
import type { PipelineError } from './utils/classifyPipelineError';
import { classifyPipelineError } from './utils/classifyPipelineError';
import type { PipelineErrorSource } from './utils/classifyPipelineError';

// Define the interface for the context
interface RecordingContextType {
  currentTranscriptionSessionId: string;
  recordingStatus: RecordingStatus;
  noteInformation: NoteInformation;
  setNoteInformation: React.Dispatch<React.SetStateAction<NoteInformation>>;
  setUseSharedMedia: React.Dispatch<React.SetStateAction<boolean>>;
  useSharedMedia: boolean;
  noAudioStatus: boolean;
  checkMicrophonePermission: () => Promise<boolean>;
  setSelectedMicrophone: (selectedMicrophone: MediaDeviceInfo) => void;
  setNoAudioStatus: (status: boolean) => void;
  getAvailableMicrophones: () => void;
  initiateRecording: () => Promise<boolean>;
  initiateEndRecording: () => Promise<boolean>;
  sendRecording: () => Promise<{ note_id: string; thread_id?: string } | undefined>;
  selectedMicrophone?: MediaDeviceInfo;
  availableMicrophones?: MediaDeviceInfo[];
  microphonePermissionGranted?: boolean;
  pauseRecording: () => void; // pause
  resumeRecording: () => Promise<boolean>; // resume
  sharedAudioEnabled?: boolean;
  setSharedAudioEnabled?: (enabled: boolean) => void;
  deleteRecordingHelper: () => Promise<boolean>;
  startTimer: () => void;
  stopTimer: () => void;
  resetTimer: () => void;
  elapsedTime: number;
  cleanUpSession: () => void;
  softResetForNavigation: () => void;
  releaseMicrophone: () => void;
  lowDataMode: boolean;
  sendMessageToExtension: (message: any) => void;
  setShouldBeCleanedUp: (value: boolean) => void;
  allowRecorderControl: boolean;
  takeOverRecording: () => Promise<boolean>;
  rehydrateStateFromLocalStorage: () => void;
  checkRecordingOwnership: () => void;
  getRecordingTimes: () => { startTime: Date; endTime: Date; elapsedTime: number } | null;
  startMicTest: () => Promise<boolean>;
  stopMicTest: () => void;
  isMicTesting: boolean;
  onS3UploadError?: (error: string) => void;
  setOnS3UploadError: (callback: ((error: string) => void) | undefined) => void;
  onRecordingFinalized?: (transcriptionSessionId: string, elapsedTime: number) => Promise<void>;
  setOnRecordingFinalized: (
    callback: ((transcriptionSessionId: string, elapsedTime: number) => Promise<void>) | undefined,
  ) => void;
  setOnRecordingStopped: (
    callback: ((transcriptionSessionId: string, elapsedTime: number) => Promise<void>) | undefined,
  ) => void;
  /**
   * GDN-2784 before-start gate. Registered by the encounter surface and
   * awaited at the top of initiateRecording — return false to abort the
   * start (e.g. the user declined recording into a future-dated encounter).
   * Covers every entry path that starts a recording, including telehealth
   * auto-start. Unregistered means no gate (the onboarding test-note
   * recorder registers nothing). The key identifies the registration (the
   * bound encounter): a start attempt that finds the gate latched by a
   * DIFFERENT key parks behind that stale holder instead of being dropped,
   * while a duplicate attempt under the same key is declined.
   */
  setBeforeRecordingStart: (
    callback: (() => Promise<boolean>) | undefined,
    key?: string,
  ) => void;
  /** True while a start attempt is held at the before-start gate. */
  beforeStartGatePending: boolean;
  requestRemoteStop: () => Promise<{ success: boolean; reason?: string }>;
  requestRemotePause: () => Promise<{ success: boolean; reason?: string }>;
  requestRemoteResume: () => Promise<{ success: boolean; reason?: string }>;
  remoteStopPending: boolean;
  audioHealthState: HealthState;
  releaseAudioStream: () => void;
  needsMicPermission: boolean;
  setAudioInputActive: (active: boolean) => void;
  setRecordingEncounterId: (id: string | null) => void;
  getRecordingEncounterId: () => string | null;
  setTelehealthSession: (
    config: { dailyCallService: DailyCallService; roomUrl: string; token: string } | null,
  ) => void;
  cleanupTelehealthSession: () => Promise<void>;
  /** Read-only access to AudioInputDeviceService for device enumeration/selection (telehealth device UIs) */
  audioInputDeviceService: AudioInputDeviceService | null;
  // Pipeline error state (service initialization failures surfaced to users)
  resetRecordingState: () => void;
  pipelineError: PipelineError | null;
  retryPipelineInit: () => void;
  // Read-only service instances for useSyncExternalStore hooks (UI components)
  sessionCompletionService: SessionCompletionService | null;
  sessionRecoveryService: SessionRecoveryService | null;
  uploadService: UploadService | null;
  recordingService: RecordingService | null;
  storageService: StorageService | null;
}

const RECORDING_CHUNK_LENGTH_SECONDS = 90; // 90 secs
const MAX_RECORDING_LENGTH_SECONDS = 60 * 60 * 4; // 4 hours

// Create the Recording context
const RecordingContext = createContext<RecordingContextType | undefined>(undefined);

// Custom hook to use the Recording context
export function useRecordingContext(): RecordingContextType {
  const context = useContext(RecordingContext);

  if (!context) {
    throw new Error('useRecordingContext must be used within an RecordingContextProvider');
  }
  return context;
}

/** Classify recording start errors for Statsig tracking. */
function classifyStartError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (msg.includes('Not ready to record')) return 'not_ready';
  if (msg.includes('Cannot start recording in state:')) return 'recording_service_state_error';
  if (msg.includes('Failed to get transcription session ID')) return 'api_session_failed';
  if (msg.includes('Failed to create media recorder')) return 'media_recorder_failed';
  if (err instanceof DOMException) {
    if (err.name === 'NotAllowedError') return 'mic_permission_denied';
    if (err.name === 'NotFoundError') return 'mic_not_found';
    if (err.name === 'NotReadableError') return 'mic_not_readable';
  }
  return 'unknown';
}

/** Subscribe to DailyCallService track events and wire remote audio into the mixer. */
function wireDailyTrackEvents(
  dailyCallService: DailyCallService,
  mixerRef: React.MutableRefObject<AudioMixerResult | null>,
): () => void {
  const onTrackStarted = ({ sessionId, type, track }: DailyCallEventMap['trackStarted']) => {
    if (type !== 'audio') return;
    // Filter local tracks: state is updated before emit, so remote tracks are in the map
    if (!dailyCallService.getSnapshot().remoteAudioTracks.has(sessionId)) return;
    mixerRef.current?.connectRemoteTrack(sessionId, track);
    SentryService.addBreadcrumb({
      category: 'telehealth.mixer',
      message: `Connected remote audio: ${sessionId}`,
      level: 'info',
    });
  };

  const onTrackStopped = ({ sessionId, type }: DailyCallEventMap['trackStopped']) => {
    if (type !== 'audio') return;
    // disconnectRemoteTrack is idempotent — safe to call for local (no-op)
    mixerRef.current?.disconnectRemoteTrack(sessionId);
    SentryService.addBreadcrumb({
      category: 'telehealth.mixer',
      message: `Disconnected remote audio: ${sessionId}`,
      level: 'info',
    });
  };

  const onParticipantLeft = ({ sessionId }: DailyCallEventMap['participantLeft']) => {
    mixerRef.current?.disconnectRemoteTrack(sessionId);
    SentryService.addBreadcrumb({
      category: 'telehealth.mixer',
      message: `Participant left: ${sessionId}`,
      level: 'info',
    });
  };

  const onStatusChanged = ({ status, prev }: DailyCallEventMap['statusChanged']) => {
    if (prev === 'connected' && status === 'idle') {
      // Call ended — disconnect all remote audio, recording continues
      const snapshot = dailyCallService.getSnapshot();
      snapshot.remoteAudioTracks.forEach((_, participantId) => {
        mixerRef.current?.disconnectRemoteTrack(participantId);
      });
      SentryService.addBreadcrumb({
        category: 'telehealth.mixer',
        message: 'Call ended — disconnected all remote audio',
        level: 'info',
      });
    }
  };

  dailyCallService.on('trackStarted', onTrackStarted);
  dailyCallService.on('trackStopped', onTrackStopped);
  dailyCallService.on('participantLeft', onParticipantLeft);
  dailyCallService.on('statusChanged', onStatusChanged);

  return () => {
    dailyCallService.off('trackStarted', onTrackStarted);
    dailyCallService.off('trackStopped', onTrackStopped);
    dailyCallService.off('participantLeft', onParticipantLeft);
    dailyCallService.off('statusChanged', onStatusChanged);
  };
}

// Define the props for the RecordingContextProvider
interface RecordingContextProviderProps {
  children: ReactNode;
}

// Create the RecordingContextProvider component
export const RecordingContextProvider: React.FC<RecordingContextProviderProps> = ({ children }) => {
  const { getAccessToken } = useAccessToken();
  const isAuthenticated = useIsAuthenticated();
  const templatesList = useTemplatesList({
    skip: !isAuthenticated,
  });
  const { setAudioData, setPaused } = useAudioDataContext();
  const { enqueueSnackbar } = useSnackbar();
  const { state: uiState, showAlertBanner } = useUIState();
  const { loadedInChromeExtension } = uiState;

  // Define state variables
  const [recordingStatus, setRecordingStatus] = useLocalStorageState<RecordingStatus>(
    'recordingStatus',
    {
      isRecording: false,
      isReuploading: false,
      isStopping: false,
      isStarting: false,
      chunksToBeUploaded: false,
      recordingPaused: false,
      readyToRecord: true,
      readyToSubmit: false,
    },
  );

  const micTestStreamRef = useRef<MediaStream | null>(null);
  const micTestLevelMeterRef = useRef<MicLevelMeter | null>(null);

  const isRecordingEnding = useRef<boolean>(false);
  const shouldBeCleanedUp = useRef<boolean>(false);

  const [lowDataMode, setLowDataMode] = useState(false);

  const [noteInformation, setNoteInformation] = useLocalStorageState<NoteInformation>(
    'noteInformation',
    {},
  );

  const [useSharedMedia, setUseSharedMedia] = useLocalStorageState<boolean>(
    'useSharedMedia',
    false,
  );

  const [currentTranscriptionSessionId, setCurrentTranscriptionSessionId] =
    useLocalStorageState<string>('transcriptionSessionId', '');

  const [audioErrorMessage, setAudioErrorMessage] = useState<string>('');
  const [noAudioStatus, setNoAudioStatus] = useLocalStorageState<boolean>('noAudioStatus', false);
  const [microphonePermissionGranted, setMicrophonePermissionGranted] = useLocalStorageState<
    boolean | undefined
  >('microphonePermissionGranted', undefined);
  const [availableMicrophones, setAvailableMicrophones] = useLocalStorageState<MediaDeviceInfo[]>(
    'availableMicrophones',
    [],
  );
  const [selectedMicrophone, setSelectedMicrophoneState] = useLocalStorageState<
    MediaDeviceInfo | undefined
  >('selectedMicrophone', undefined);

  const elapsedTime = useRef(0);
  // State mirror of elapsedTime for display. The ref alone never triggers a
  // render, so the timer UI would only update when something else (e.g. the
  // 50ms audio-level state) happened to re-render the provider — and freezes
  // entirely on silent input, where the level meter emits a constant 0.
  // Every write to elapsedTime.current must also set this state.
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Store actual recording start time for accurate timestamps
  const recordingStartTime = useRef<number | null>(null);

  const currentRecordingSession = useRef<RecordingSession | undefined>(undefined);

  // Ref to store transcription session ID as a fallback for stale closure issues.
  // IMPORTANT: This exists because requestRemoteStop uses useCallback with empty deps,
  // which can cause it to call performStopRecording with a stale closure that has
  // an empty currentTranscriptionSessionId. This is especially problematic on Safari
  // where localStorage may also be unreliable. The ref provides a stable reference
  // that doesn't suffer from stale closure issues.
  const transcriptionSessionIdRef = useRef<string>('');

  // Ref to always hold the latest performStopRecording function.
  // requestRemoteStop uses useCallback([]) which creates a stale closure —
  // calling through this ref ensures we always invoke the current version.
  const performStopRecordingRef = useRef<() => Promise<boolean>>(async () => false);
  const performPauseRecordingRef = useRef<() => boolean>(() => false);
  const performResumeRecordingRef = useRef<() => Promise<boolean>>(async () => false);

  const isLoadedInChromeExtension = useRef(false);

  // Cleanup function for shared media stream mixing (telehealth/headphones mode)
  const streamCleanupRef = useRef<(() => void) | null>(null);
  // Mixer result for mid-recording device switching (Phase 3b)
  const mixerResultRef = useRef<AudioMixerResult | null>(null);

  // Telehealth refs (Phase 3c)
  const dailyCallServiceRef = useRef<DailyCallService | null>(null);
  const deviceBridgeRef = useRef<DeviceBridgeService | null>(null);
  const telehealthConfigRef = useRef<{ roomUrl: string; token: string } | null>(null);
  const dailyEventCleanupRef = useRef<(() => void) | null>(null);

  // Added state for appointment type
  const [appointmentType, setAppointmentType] = useLocalStorageState<string>(
    'appointmentType',
    'inPerson',
  );

  const [allowRecorderControl, setAllowRecorderControl] = useState<boolean>(true);
  const [remoteStopPending, setRemoteStopPending] = useState<boolean>(false);

  const [isMicTesting, setIsMicTesting] = useState<boolean>(false);

  const [storageInfo, setStorageInfo] = useState<{
    usagePercent: number;
    availableMB: number;
    quotaMB: number;
    minimumRequiredQuotaMB: number;
  } | null>(null);
  const [showStorageCleanupDialog, setShowStorageCleanupDialog] = useState(false);

  // --- Permanent AudioInputDeviceService integration ---
  const {
    audioDevice: audioDeviceTracking,
    recording: recordingTracking,
    sessionCompletion: sessionCompletionTracking,
    sessionRecovery: sessionRecoveryTracking,
    recordingUI: recordingUITracking,
    error: errorTracking,
  } = useEventTracking();
  const audioDeviceServiceRef = useRef<AudioInputDeviceService | null>(null);
  const [audioHealthState, setAudioHealthState] = useState<HealthState>('idle');
  const [needsMicPermission, setNeedsMicPermission] = useState(false);
  const [audioInputActive, setAudioInputActiveState] = useState(false);
  const setAudioInputActive = useCallback((active: boolean) => {
    setAudioInputActiveState(active);
  }, []);
  // Refs to avoid stale closures in service event handlers
  const isRecordingRef = useRef(false);
  const failedDevicesRef = useRef<Set<string>>(new Set());

  // --- Permanent StorageService integration ---
  const storageServiceRef = useRef<StorageService | null>(null);
  // --- Permanent UploadService integration ---
  const uploadServiceRef = useRef<UploadService | null>(null);
  const enqueuedChunkCountRef = useRef(0);

  // --- Permanent RecordingService integration ---
  const recordingServiceRef = useRef<RecordingService | null>(null);

  // --- Permanent SessionCompletionService integration (ADR-007) ---
  const sessionCompletionServiceRef = useRef<SessionCompletionService | null>(null);
  const sessionRecoveryServiceRef = useRef<SessionRecoveryService | null>(null);
  const sessionManifestRef = useRef<SessionManifest | null>(null);

  // Sync the permanent recording pipeline state to Sentry for observability.
  useEffect(() => {
    SentryService.setTag('recording_pipeline', 'streaming');

    return () => {
      SentryService.setTag('recording_pipeline', '');
    };
  }, []);

  // --- Exposed service instances for UI hooks (useSyncExternalStore) ---
  const [exposedUploadService, setExposedUploadService] = useState<UploadService | null>(null);
  const [exposedRecordingService, setExposedRecordingService] = useState<RecordingService | null>(
    null,
  );
  const [exposedStorageService, setExposedStorageService] = useState<StorageService | null>(null);
  const [exposedCompletionService, setExposedCompletionService] =
    useState<SessionCompletionService | null>(null);
  const [exposedRecoveryService, setExposedRecoveryService] =
    useState<SessionRecoveryService | null>(null);
  const [exposedAudioDeviceService, setExposedAudioDeviceService] =
    useState<AudioInputDeviceService | null>(null);

  // --- Pipeline error state (service initialization failures surfaced to users) ---
  const [pipelineError, setPipelineError] = useState<PipelineError | null>(null);
  const pipelineRetryCountRef = useRef(0);

  /**
   * Surface a classified pipeline error without falling back to retired storage or recording paths.
   * Retry restores the permanent service composition.
   */
  const disableStreamingPipeline = useCallback(
    (source: PipelineErrorSource, error: unknown) => {
      const classified = classifyPipelineError(source, error, pipelineRetryCountRef.current);

      setPipelineError(classified);

      SentryService.addBreadcrumb({
        category: 'pipeline.init',
        message: `Pipeline error: ${classified.type} from ${source}`,
        level: 'error',
        data: {
          error_type: classified.type,
          source,
          retry_count: pipelineRetryCountRef.current,
        },
      });

      // Track with Statsig
      errorTracking.pipelineInit({
        error_type: classified.type,
        error_source: source,
        error_message: classified.message,
        error_name: error instanceof Error ? error.name : undefined,
        retry_count: pipelineRetryCountRef.current,
        encounter_id: recordingEncounterIdRef.current ?? undefined,
      });
    },
    [errorTracking],
  );

  /**
   * Retry permanent storage initialization after a failure.
   */
  const retryPipelineInit = useCallback(() => {
    if (pipelineRetryCountRef.current >= 3) return;
    const service = storageServiceRef.current;
    if (!service) return;

    pipelineRetryCountRef.current += 1;
    setPipelineError(null);
    void service
      .initialize()
      .then(() => {
        pipelineRetryCountRef.current = 0;
      })
      .catch(error => {
        disableStreamingPipeline('storage_init', error);
      });
  }, [disableStreamingPipeline]);

  // S3 upload error callback
  const [onS3UploadError, setOnS3UploadErrorState] = useState<
    ((error: string) => void) | undefined
  >(undefined);

  // Wrapper to properly set the callback function
  const setOnS3UploadError = useCallback((callback: ((error: string) => void) | undefined) => {
    setOnS3UploadErrorState(() => callback);
  }, []);
  // Ref to avoid stale closure in UploadService event handlers
  const onS3UploadErrorRef = useRef(onS3UploadError);
  useEffect(() => {
    onS3UploadErrorRef.current = onS3UploadError;
  }, [onS3UploadError]);

  // Recording finalized callback - called when recording ends and document should be created
  const onRecordingFinalizedRef = useRef<
    ((transcriptionSessionId: string, elapsedTime: number) => Promise<void>) | undefined
  >(undefined);

  // Wrapper to properly set the callback function
  const setOnRecordingFinalized = useCallback(
    (
      callback:
        | ((transcriptionSessionId: string, elapsedTime: number) => Promise<void>)
        | undefined,
    ) => {
      onRecordingFinalizedRef.current = callback;
    },
    [],
  );

  // Callback invoked synchronously after recording stops and readyToSubmit is set.
  // Used by EncounterViewV2 to create the recording document before any cleanup runs.
  const onRecordingStoppedRef = useRef<
    ((transcriptionSessionId: string, elapsedTime: number) => Promise<void>) | undefined
  >(undefined);

  const setOnRecordingStopped = useCallback(
    (
      callback:
        | ((transcriptionSessionId: string, elapsedTime: number) => Promise<void>)
        | undefined,
    ) => {
      onRecordingStoppedRef.current = callback;
    },
    [],
  );

  // GDN-2784 before-start gate — registered by EncounterViewV2, awaited at
  // the top of initiateRecording so a decline aborts before any recording
  // machinery spins up.
  const beforeRecordingStartRef = useRef<(() => Promise<boolean>) | undefined>(undefined);
  const beforeRecordingStartKeyRef = useRef<string | undefined>(undefined);
  // Latch (logic) + state (render): only one start may hold the gate, and
  // surfaces can disable their start affordances while it is held. The latch
  // records the registration key its gate ran under so an attempt arriving
  // after navigation can distinguish a stale predecessor (parks behind it —
  // stale gates fail closed within one poll tick) from a duplicate attempt
  // under the same registration (declined).
  const beforeStartGateInFlightRef = useRef<{
    key: string | undefined;
    promise: Promise<boolean>;
  } | null>(null);
  const [beforeStartGatePending, setBeforeStartGatePending] = useState(false);
  const setBeforeRecordingStart = useCallback(
    (callback: (() => Promise<boolean>) | undefined, key?: string) => {
      beforeRecordingStartRef.current = callback;
      beforeRecordingStartKeyRef.current = callback ? key : undefined;
    },
    [],
  );

  // Encounter ID ref — set by EncounterViewV2, read at manifest creation time
  const recordingEncounterIdRef = useRef<string | null>(null);
  const setRecordingEncounterId = useCallback((id: string | null) => {
    recordingEncounterIdRef.current = id;
  }, []);
  const getRecordingEncounterId = useCallback(() => recordingEncounterIdRef.current, []);

  // Telehealth session setter — called by EncounterViewV2 when entering a telehealth encounter
  const setTelehealthSession = useCallback(
    (config: { dailyCallService: DailyCallService; roomUrl: string; token: string } | null) => {
      if (config) {
        dailyCallServiceRef.current = config.dailyCallService;
        telehealthConfigRef.current = { roomUrl: config.roomUrl, token: config.token };
      } else {
        dailyCallServiceRef.current = null;
        telehealthConfigRef.current = null;
      }
    },
    [],
  );

  // Full telehealth teardown — called by Phase 4 UI when navigating away from encounter
  const cleanupTelehealthSession = useCallback(async () => {
    // 1. Event subscriptions
    dailyEventCleanupRef.current?.();
    dailyEventCleanupRef.current = null;

    // 2. DeviceBridgeService (stops call track, clears provider)
    deviceBridgeRef.current?.dispose();
    deviceBridgeRef.current = null;

    // 3. DailyCallService leave + destroy
    if (dailyCallServiceRef.current) {
      const service = dailyCallServiceRef.current;
      try {
        await service.leave();
      } catch (err) {
        SentryService.addBreadcrumb({
          category: 'telehealth.cleanup',
          message: `Daily leave error: ${err}`,
          level: 'warning',
        });
      }

      try {
        await service.destroy();
      } catch (err) {
        SentryService.addBreadcrumb({
          category: 'telehealth.cleanup',
          message: `Daily destroy error: ${err}`,
          level: 'warning',
        });
      }
    }

    // 4. Clear refs and config
    dailyCallServiceRef.current = null;
    telehealthConfigRef.current = null;
    SentryService.setTag('telehealth_active', '');
  }, []);

  /**
   * Constraint override for recording-capture acquisition. Telehealth
   * recordings enable echo cancellation: the provider's speakers play the
   * patient's voice while the mic records, and without AEC the recording
   * hears that voice twice — acoustically here and digitally via the Daily
   * remote-track mixer. In-person recordings keep the raw default (see
   * StreamAcquirer DEFAULT_CONSTRAINTS) — there is no far-end render to
   * cancel, and raw capture maximizes transcription fidelity.
   */
  const getRecordingAcquireConstraints = useCallback(
    (): AudioConstraints | undefined =>
      dailyCallServiceRef.current && telehealthConfigRef.current
        ? { echoCancellation: true }
        : undefined,
    [],
  );

  const rehydrateStateFromLocalStorage = useCallback(() => {
    console.log('Rehydrating state from localStorage');

    // Rehydrate recordingStatus
    const storedRecordingStatus = localStorage.getItem('recordingStatus');
    if (storedRecordingStatus) {
      try {
        const parsedStatus = JSON.parse(storedRecordingStatus);
        setRecordingStatus(parsedStatus);
      } catch (e) {
        console.error('Error parsing recordingStatus from localStorage', e);
      }
    }

    // Rehydrate noteInformation
    const storedNoteInfo = localStorage.getItem('noteInformation');
    if (storedNoteInfo) {
      try {
        const parsedNoteInfo = JSON.parse(storedNoteInfo);
        setNoteInformation(parsedNoteInfo);
      } catch (e) {
        console.error('Error parsing noteInformation from localStorage', e);
      }
    }

    // Rehydrate useSharedMedia
    const storedUseSharedMedia = localStorage.getItem('useSharedMedia');
    if (storedUseSharedMedia) {
      try {
        const parsedUseSharedMedia = JSON.parse(storedUseSharedMedia);
        setUseSharedMedia(parsedUseSharedMedia);
      } catch (e) {
        console.error('Error parsing useSharedMedia from localStorage', e);
      }
    }

    // Rehydrate currentTranscriptionSessionId
    const storedTranscriptionSessionId = localStorage.getItem('transcriptionSessionId');
    if (storedTranscriptionSessionId) {
      try {
        const parsedSessionId = JSON.parse(storedTranscriptionSessionId);
        setCurrentTranscriptionSessionId(parsedSessionId);
        // Also set the ref for fallback purposes
        transcriptionSessionIdRef.current = parsedSessionId;
      } catch (e) {
        console.error('Error parsing transcriptionSessionId from localStorage', e);
      }
    }

    // Rehydrate noAudioStatus
    const storedNoAudioStatus = localStorage.getItem('noAudioStatus');
    if (storedNoAudioStatus) {
      try {
        const parsedNoAudioStatus = JSON.parse(storedNoAudioStatus);
        setNoAudioStatus(parsedNoAudioStatus);
      } catch (e) {
        console.error('Error parsing noAudioStatus from localStorage', e);
      }
    }

    // Rehydrate microphonePermissionGranted
    const storedMicPermission = localStorage.getItem('microphonePermissionGranted');
    if (storedMicPermission) {
      try {
        const parsedMicPermission = JSON.parse(storedMicPermission);
        setMicrophonePermissionGranted(parsedMicPermission);
      } catch (e) {
        console.error('Error parsing microphonePermissionGranted from localStorage', e);
      }
    }

    // AudioInputDeviceService owns device discovery and preference restoration.

    // Rehydrate appointmentType
    const storedAppointmentType = localStorage.getItem('appointmentType');
    if (storedAppointmentType) {
      try {
        const parsedAppointmentType = JSON.parse(storedAppointmentType);
        setAppointmentType(parsedAppointmentType);
      } catch (e) {
        console.error('Error parsing appointmentType from localStorage', e);
      }
    }

    // Rehydrate elapsedTime
    const storedElapsedTime = localStorage.getItem('elapsedTime');
    if (storedElapsedTime) {
      try {
        elapsedTime.current = parseInt(storedElapsedTime, 10);
        setElapsedSeconds(elapsedTime.current);
      } catch (e) {
        console.error('Error parsing elapsedTime from localStorage', e);
      }
    }

    // Attempt to recover recording session
    attemptRecoverRecordingSession();

    console.log('State rehydration from localStorage complete');
  }, [
    setRecordingStatus,
    setNoteInformation,
    setUseSharedMedia,
    setCurrentTranscriptionSessionId,
    setNoAudioStatus,
    setMicrophonePermissionGranted,
    setAvailableMicrophones,
    setSelectedMicrophoneState,
    setAppointmentType,
  ]);

  const sendMessageToExtension = (message: any) => {
    if (loadedInChromeExtension) {
      console.log('Sending message to extension...');
      window.parent.postMessage(message, '*');
    }
  };

  // Start the recording timer
  const startTimer = () => {
    if (timerRef.current) return;
    timerRef.current = setInterval(() => {
      elapsedTime.current = elapsedTime.current + 1;
      setElapsedSeconds(elapsedTime.current);
    }, 1000);
  };

  // Stop the recording timer
  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Reset the recording timer
  const resetTimer = () => {
    stopTimer();
    elapsedTime.current = 0;
    setElapsedSeconds(0);
    recordingStartTime.current = null;
    localStorage.setItem('elapsedTime', '0');
    setAudioErrorMessage('');
  };

  useEffect(() => {
    sendMessageToExtension({
      type: 'audioErrorMessage',
      data: audioErrorMessage,
    });
  }, [audioErrorMessage]);
  const enterLowDataMode = () => {
    setLowDataMode(true);

    console.log('Entering low data mode.');
    currentRecordingSession.current?.addLog('ENTERING LOW DATA MODE');
    SentryService.logEvent('Entering low data mode.', {
      level: 'info',
      extra: { transcriptionSessionId: currentTranscriptionSessionId },
    });
  };

  // Internal function to perform the actual pause recording logic
  const performPauseRecording = (): boolean => {
    // RecordingService flushes accumulated data and pauses MediaRecorder.
    if (!recordingServiceRef.current) {
      SentryService.logEvent('RecordingService unavailable while pausing', {
        level: 'error',
        extra: { transcriptionSessionId: transcriptionSessionIdRef.current },
      });
      return false;
    }
    recordingServiceRef.current.pause();

    setRecordingStatus(prev => ({
      ...prev,
      recordingPaused: true,
    }));

    // Pause audio history updates
    setPaused(true);
    return true;
  };

  // Keep ref in sync so requestRemotePause (stale useCallback) always calls the latest version
  performPauseRecordingRef.current = performPauseRecording;

  // Pause the recording
  const pauseRecording = () => {
    // Only allow the owner tab to pause the recording
    if (!allowRecorderControl) {
      enqueueSnackbar('Only the tab that started the recording can control it', {
        variant: 'warning',
      });
      return;
    }

    performPauseRecording();
  };

  // Internal function to perform the actual resume recording logic
  const performResumeRecording = async (): Promise<boolean> => {
    try {
      if (!recordingServiceRef.current) {
        throw new Error('RecordingService unavailable while resuming');
      }
      await recordingServiceRef.current.resume();

      setRecordingStatus(prev => ({
        ...prev,
        recordingPaused: false,
      }));

      // Resume audio history updates
      setPaused(false);
      return true;
    } catch (error) {
      console.error('Error resuming recording:', error);
      SentryService.logEvent('Error resuming recording.', {
        level: 'error',
        extra: { error },
      });
      setAudioErrorMessage('Failed to resume recording. Try again.');
      enqueueSnackbar('Failed to resume recording. Try again.', {
        variant: 'error',
      });
      setTimeout(() => setAudioErrorMessage(''), 3000);
      return false;
    }
  };

  // Keep ref in sync so requestRemoteResume (stale useCallback) always calls the latest version
  performResumeRecordingRef.current = performResumeRecording;

  // Resume the recording
  const resumeRecording = async (): Promise<boolean> => {
    // Only allow the owner tab to resume the recording
    if (!allowRecorderControl) {
      enqueueSnackbar('Only the tab that started the recording can control it', {
        variant: 'warning',
      });
      return false;
    }

    return performResumeRecording();
  };

  const setShouldBeCleanedUp = (value: boolean) => {
    shouldBeCleanedUp.current = value;
  };

  // --- Recording service ref sync effects ---
  useEffect(() => {
    isRecordingRef.current = recordingStatus.isRecording;
  }, [recordingStatus.isRecording]);

  // Initialize/dispose the permanent StorageService.
  useEffect(() => {
    let disposed = false;
    const service = new StorageService();
    storageServiceRef.current = service;
    setExposedStorageService(service);

    service
      .initialize()
      .then(() => {
        if (disposed) {
          service.dispose();
          return;
        }
        // Reset retry count on successful init
        pipelineRetryCountRef.current = 0;
        SentryService.addBreadcrumb({
          category: 'storage.service',
          message: `StorageService ready, backend: ${service.getSnapshot().backendType}`,
          level: 'info',
        });
      })
      .catch((error: unknown) => {
        if (disposed) return;
        SentryService.captureException(error instanceof Error ? error : new Error(String(error)), {
          level: 'error',
          tags: { component: 'RecordingContext', ...storageErrorTags(error) },
          extra: { source: 'storage_init' },
        });
        disableStreamingPipeline('storage_init', error);
      });

    service.on('quotaWarning', ({ usage, quota }) => {
      SentryService.addBreadcrumb({
        category: 'storage.quota',
        message: `Storage quota warning: ${Math.round(usage / 1024 / 1024)}MB / ${Math.round(quota / 1024 / 1024)}MB`,
        level: 'warning',
      });
    });

    return () => {
      disposed = true;
      service.dispose();
      storageServiceRef.current = null;
      setExposedStorageService(null);
    };
  }, [disableStreamingPipeline]);

  // Dispose AudioInputDeviceService on unmount
  useEffect(() => {
    return () => {
      if (audioDeviceServiceRef.current) {
        audioDeviceServiceRef.current.dispose();
        audioDeviceServiceRef.current = null;
        setExposedAudioDeviceService(null);
      }
    };
  }, []);

  /** Lazily create AudioInputDeviceService and wire event listeners */
  const getOrCreateAudioService = useCallback((): AudioInputDeviceService => {
    if (!audioDeviceServiceRef.current) {
      const service = new AudioInputDeviceService();
      audioDeviceServiceRef.current = service;
      setExposedAudioDeviceService(service);

      service.on('healthChanged', ({ state, prev }: { state: HealthState; prev: HealthState }) => {
        setAudioHealthState(state);
        // Clear failed-device memory when a device proves healthy
        if (state === 'healthy') {
          failedDevicesRef.current.clear();
        }
        audioDeviceTracking.healthChanged({
          from_state: prev,
          to_state: state,
          during_recording: isRecordingRef.current,
        });

        // GDN-2205: degraded/failed were previously only a Statsig event +
        // Sentry breadcrumb — promote to a real Sentry event so silent-mic
        // sessions are alertable/queryable with the device profile attached.
        if (state === 'degraded' || state === 'failed') {
          SentryService.logEvent(`Audio health ${state}`, {
            level: state === 'failed' ? 'error' : 'warning',
            tags: { audio_health: state },
            extra: {
              from_state: prev,
              during_recording: isRecordingRef.current,
              transcriptionSessionId: transcriptionSessionIdRef.current,
            },
          });
        }
      });

      // perfSample events are NOT forwarded to Statsig — they fire every 250ms
      // and would generate ~7,200 events per 30-min session. The aggregated
      // audio_performance_summary (1 event per session) provides the same data.

      service.on('performanceSummary', summary => {
        audioDeviceTracking.performanceSummary({
          ...summary,
        });
      });

      service.on('devicesChanged', (diff: DeviceDiff) => {
        setAvailableMicrophones(service.getDevices().map(normalizedToMediaDeviceInfo));
        audioDeviceTracking.deviceChanged({
          added_count: diff.added.length,
          removed_count: diff.removed.length,
          changed_count: diff.changed.length,
          during_recording: isRecordingRef.current,
        });
      });

      service.on('deviceLost', (device: NormalizedDevice) => {
        audioDeviceTracking.deviceLost({
          device_transport: device.transport,
          during_recording: isRecordingRef.current,
        });
      });

      service.on('permissionChanged', (result: AudioPermissionResult) => {
        audioDeviceTracking.permissionResult({
          granted: result.granted,
          error_type: result.error,
        });
      });

      // Bridge the service's MicLevelMeter (honest time-domain level, NOT the
      // health monitor's dB-frequency average) → AudioDataContext for VU meter
      // visualization. Only feeds data when NOT recording; RecordingService
      // owns the mixed-stream level while recording.
      service.setOnLevelChanged((level: number) => {
        if (!isRecordingRef.current) {
          setAudioData(level);
        }
      });
    }
    return audioDeviceServiceRef.current;
  }, [setAvailableMicrophones, setAudioData]);

  // Encounter-only initialization: RecordingContext is mounted for the whole authenticated app,
  // so microphone acquisition must remain inactive until an Encounter explicitly activates it.
  // Once active, silently check cached permission, enumerate devices, and acquire the stream.
  useEffect(() => {
    if (!audioInputActive) return;

    const service = getOrCreateAudioService();

    let cancelled = false;

    (async () => {
      try {
        if (!service.isInitialized()) {
          // Silent check — uses permissions.query(), no browser prompt, works without user gesture
          const permission = await service.initializeSilent();
          if (cancelled) return;

          if (!permission.granted) {
            // Permission not cached — show overlay so user can click to grant
            setNeedsMicPermission(true);
            return;
          }
        }

        // Silent check succeeded — permission was cached in browser
        setNeedsMicPermission(false);
        setMicrophonePermissionGranted(true);

        // Sync normalized device list
        const devices = service.getDevices();
        const mediaDevices = devices.map(normalizedToMediaDeviceInfo);
        setAvailableMicrophones(mediaDevices);

        // Restore preference and determine target device
        let targetDevice: NormalizedDevice | undefined;
        const pref = service.getPreferenceStore().load();
        if (pref && devices.length > 0) {
          const match = service.getPreferenceStore().findBestMatch(pref, devices);
          if (match) {
            setSelectedMicrophoneState(normalizedToMediaDeviceInfo(match));
            localStorage.setItem('selectedMicrophoneId', match.browserDeviceId);
            targetDevice = match;
          }
        }

        if (cancelled) return;

        // Auto-acquire stream — mic is now live, health monitoring starts,
        // and level data flows to AudioDataContext for the VU meter.
        if (service.getActiveStream()) return;
        const acquireResult = await service.acquire(targetDevice?.internalId);

        // Track auto-acquisition for analytics
        audioDeviceTracking.micAutoAcquired?.({
          microphone_id: acquireResult.device.browserDeviceId,
          device_transport: acquireResult.device.transport,
        });
      } catch (error) {
        // On any error, show the overlay as a fallback so user can manually trigger permission
        if (!cancelled) setNeedsMicPermission(true);
        console.warn('Waiting room audio init failed (non-fatal)', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    audioInputActive,
    getOrCreateAudioService,
    setAvailableMicrophones,
    setMicrophonePermissionGranted,
  ]);

  // Auto-select best available mic when the selected device disappears (e.g., Bluetooth disconnect)
  useEffect(() => {
    // Telehealth recordings can continue in the floating panel after Encounter unmounts.
    // Keep mid-recording device hot-swap alive even when idle acquisition is route-inactive.
    if (!audioInputActive && !isRecordingRef.current) return;
    if (!selectedMicrophone || availableMicrophones.length === 0) return;

    const stillAvailable = availableMicrophones.some(
      m => m.deviceId === selectedMicrophone.deviceId,
    );

    if (!stillAvailable) {
      const best = availableMicrophones[0]; // Already sorted by priority (builtin > usb > bluetooth)
      setSelectedMicrophoneState(best);
      localStorage.setItem('selectedMicrophoneId', best.deviceId);
      enqueueSnackbar(
        `${selectedMicrophone.label || 'Microphone'} disconnected. Switched to ${best.label || 'default microphone'}.`,
        { variant: 'warning' },
      );

      if (isRecordingRef.current && mixerResultRef.current) {
        // Mid-recording: hot-swap the mixer source so the new mic feeds into the recording
        const service = audioDeviceServiceRef.current;
        if (service?.isInitialized()) {
          const device = service.getDevices().find(d => d.browserDeviceId === best.deviceId);
          if (device) {
            handleMidRecordingDeviceSwitch(device.internalId).catch(err => {
              SentryService.addBreadcrumb({
                category: 'recording.context',
                message: `Auto-disconnect mid-recording device switch failed: ${err}`,
                level: 'error',
              });
            });
          }
        }
      } else {
        // Not recording: re-acquire stream on the fallback device so health monitoring restarts
        // (triggers 'initializing' → spinner in AudioLevelMeter)
        const service = audioDeviceServiceRef.current;
        if (service?.isInitialized()) {
          const device = service.getDevices().find(d => d.browserDeviceId === best.deviceId);
          service.acquire(device?.internalId).catch(err => {
            console.warn('Failed to re-acquire stream after device switch', err);
          });
        }
      }
    }
  }, [
    audioInputActive,
    selectedMicrophone,
    availableMicrophones,
    enqueueSnackbar,
    setSelectedMicrophoneState,
  ]);

  // Auto-fallback: when health reaches 'failed' and not recording,
  // try the next available device that hasn't already failed
  useEffect(() => {
    if (!audioInputActive) return;
    if (audioHealthState !== 'failed') return;
    if (recordingStatus.isRecording) return;

    const service = audioDeviceServiceRef.current;
    if (!service?.isInitialized()) return;

    // Mark the current device as failed
    const activeStream = service.getActiveStream();
    if (activeStream) {
      failedDevicesRef.current.add(activeStream.device.internalId);
    }

    // Find next untried device
    const devices = service.getDevices();
    const candidate = devices.find(d => !failedDevicesRef.current.has(d.internalId));

    if (!candidate) return; // All devices tried — user must intervene

    // Switch UI + persist
    const candidateMedia = normalizedToMediaDeviceInfo(candidate);
    setSelectedMicrophoneState(candidateMedia);
    localStorage.setItem('selectedMicrophoneId', candidateMedia.deviceId);
    enqueueSnackbar(
      `${selectedMicrophone?.label || 'Microphone'} isn't working. Trying ${candidate.displayLabel || 'next microphone'}...`,
      { variant: 'warning' },
    );

    // Track event
    audioDeviceTracking.autoFallback({
      failed_device_label: activeStream?.device.displayLabel,
      candidate_device_label: candidate.displayLabel,
      devices_tried_count: failedDevicesRef.current.size,
    });

    // Acquire triggers healthMonitor.start() → 'initializing' → spinner
    service.acquire(candidate.internalId).catch(err => {
      console.warn('Auto-fallback acquire failed', err);
    });
  }, [
    audioInputActive,
    audioHealthState,
    recordingStatus.isRecording,
    selectedMicrophone,
    enqueueSnackbar,
    setSelectedMicrophoneState,
  ]);

  const checkMicrophonePermission = async (): Promise<boolean> => {
    try {
      const service = getOrCreateAudioService();

      if (!service.isInitialized()) {
        const permission = await service.initialize();
        if (!permission.granted) {
          setMicrophonePermissionGranted(false);
          if (!needsMicPermission) {
            const errorMessage =
              permission.error === 'not_found'
                ? 'No microphone found. Please connect a microphone to use the recorder.'
                : "Microphone access is denied. You'll need to enable it to use the recorder.";
            setAudioErrorMessage(errorMessage);
            setTimeout(() => setAudioErrorMessage(''), 3000);
            enqueueSnackbar(errorMessage, { variant: 'error' });
          }
          return false;
        }
      }

      setMicrophonePermissionGranted(true);
      setNeedsMicPermission(false);

      const devices = service.getDevices();
      const mediaDevices = devices.map(normalizedToMediaDeviceInfo);
      setAvailableMicrophones(mediaDevices);

      const pref = service.getPreferenceStore().load();
      if (pref && devices.length > 0) {
        const match = service.getPreferenceStore().findBestMatch(pref, devices);
        if (match) {
          setSelectedMicrophoneState(normalizedToMediaDeviceInfo(match));
          localStorage.setItem('selectedMicrophoneId', match.browserDeviceId);
        } else if (!selectedMicrophone && mediaDevices.length > 0) {
          setSelectedMicrophoneState(mediaDevices[0]);
          localStorage.setItem('selectedMicrophoneId', mediaDevices[0].deviceId);
        }
      } else if (!selectedMicrophone && mediaDevices.length > 0) {
        setSelectedMicrophoneState(mediaDevices[0]);
        localStorage.setItem('selectedMicrophoneId', mediaDevices[0].deviceId);
      }

      audioDeviceTracking.initialized({
        device_count: devices.length,
        permission_granted: true,
        selected_transport: devices.find(d => d.browserDeviceId === selectedMicrophone?.deviceId)
          ?.transport,
      });

      try {
        await service.acquire();
      } catch (acquireError) {
        console.warn('Pre-acquisition for health monitoring failed', acquireError);
        SentryService.addBreadcrumb({
          category: 'audio.service',
          message: 'Pre-acquire for health monitoring failed (non-fatal)',
          level: 'warning',
          data: {
            error: acquireError instanceof Error ? acquireError.message : String(acquireError),
          },
        });
      }

      return true;
    } catch (error: unknown) {
      console.error('AudioInputDeviceService initialization error', error);
      SentryService.logEvent('AudioInputDeviceService initialization error', {
        level: 'error',
        extra: { error },
      });
      setMicrophonePermissionGranted(false);
      return false;
    }
  };

  const getAvailableMicrophones = async () => {
    const service = getOrCreateAudioService();
    if (!service.isInitialized()) {
      const permission = await service.initializeSilent();
      if (!permission.granted) {
        setNeedsMicPermission(true);
        setAvailableMicrophones([]);
        return;
      }
    }

    const devices = service.getDevices();
    const mediaDevices = devices.map(normalizedToMediaDeviceInfo);
    setAvailableMicrophones(mediaDevices);

    const storedMicrophoneId = localStorage.getItem('selectedMicrophoneId');
    if (storedMicrophoneId) {
      const stored = mediaDevices.find(m => m.deviceId === storedMicrophoneId);
      if (stored) {
        setSelectedMicrophone(stored);
        return;
      }
    }

    if (mediaDevices.length > 0 && !selectedMicrophone) {
      setSelectedMicrophone(mediaDevices[0]);
    }
  };

  // Set the selected microphone and store it in localStorage
  const setSelectedMicrophone = (mic: MediaDeviceInfo) => {
    failedDevicesRef.current.clear();
    setSelectedMicrophoneState(mic);
    localStorage.setItem('selectedMicrophoneId', mic.deviceId);

    // Sync to the permanent service preference store.
    if (audioDeviceServiceRef.current?.isInitialized()) {
      const normalizedDevice = audioDeviceServiceRef.current
        .getDevices()
        .find(d => d.browserDeviceId === mic.deviceId);
      if (normalizedDevice) {
        audioDeviceServiceRef.current.getPreferenceStore().save(normalizedDevice);

        if (isRecordingRef.current && mixerResultRef.current) {
          // V5 mid-recording device switch
          handleMidRecordingDeviceSwitch(normalizedDevice.internalId).catch(err => {
            SentryService.addBreadcrumb({
              category: 'recording.context',
              message: `Mid-recording device switch failed: ${err}`,
              level: 'error',
            });
          });
        } else if (!isRecordingRef.current) {
          // Re-acquire for health monitoring on the new device (only when not recording, and only if different)
          const activeStream = audioDeviceServiceRef.current.getActiveStream();
          if (activeStream && activeStream.device.internalId !== normalizedDevice.internalId) {
            audioDeviceServiceRef.current
              .acquire(normalizedDevice.internalId)
              .then(result => {
                // If telehealth call is active (but not recording), point Daily
                // at the same device by id so it opens its own processed (AEC)
                // capture. Never hand Daily a clone of the raw health-monitoring
                // stream — without echo cancellation the patient hears their own
                // voice back.
                if (dailyCallServiceRef.current?.getCallObject()) {
                  dailyCallServiceRef.current
                    .setAudioDevice(result.device.browserDeviceId)
                    .catch(err => {
                      SentryService.addBreadcrumb({
                        category: 'telehealth.device',
                        message: `Daily mic update failed (non-recording): ${err}`,
                        level: 'warning',
                      });
                    });
                }
              })
              .catch(acquireError => {
                console.warn('Re-acquire on mic change failed (non-fatal)', acquireError);
                SentryService.addBreadcrumb({
                  category: 'audio.service',
                  message: 'Re-acquire on mic change failed',
                  level: 'warning',
                });
              });
          }
        }
      }
    }
  };

  const releaseMicrophone = () => {
    micTestLevelMeterRef.current?.stop();
    micTestStreamRef.current?.getTracks().forEach(track => track.stop());
    micTestStreamRef.current = null;
    setIsMicTesting(false);
    if (!isRecordingRef.current) {
      audioDeviceServiceRef.current?.release();
    }
  };

  /** Release the audio device service stream (e.g., on page navigation). No-op during recording. */
  const releaseAudioStream = useCallback(() => {
    if (isRecordingRef.current) return;
    if (audioDeviceServiceRef.current?.getActiveStream()) {
      audioDeviceServiceRef.current.release();
      setAudioHealthState('idle');
    }
  }, []);

  /** Mid-recording device switch: hot-swap the mixer's mic source without interrupting MediaRecorder. */
  async function handleMidRecordingDeviceSwitch(newDeviceInternalId: string) {
    // 1. Flush current chunk for clean boundary
    recordingServiceRef.current?.flush();

    // 2. Release old mic, acquire new one
    //    release() calls track.stop() — mixer's old source node produces silence
    //    acquire() calls getUserMedia — ~5-50ms gap
    audioDeviceServiceRef.current!.release();
    const acquireResult = await audioDeviceServiceRef.current!.acquire(
      newDeviceInternalId,
      getRecordingAcquireConstraints(),
    );

    // 3. Hot-swap the mixer's mic source (MediaRecorder unaffected)
    mixerResultRef.current!.swapMicSource(acquireResult.stream);

    // 4. Update RecordingService state
    const newDeviceId = acquireResult.stream.getAudioTracks()[0]?.getSettings()?.deviceId;
    recordingServiceRef.current?.updateDeviceId(newDeviceId ?? newDeviceInternalId);

    // 5. Re-activate BackgroundThrottleGuard with new raw mic stream
    //    Old raw stream tracks are dead (release() stopped them), guard needs fresh tracks
    recordingServiceRef.current?.updateRawMicStream(acquireResult.stream);

    // Phase 3c: propagate to Daily's call mic (if telehealth active)
    if (deviceBridgeRef.current) {
      const newMicTrack = acquireResult.stream.getAudioTracks()[0];
      deviceBridgeRef.current.handleDeviceSwitch(newMicTrack).catch(err => {
        SentryService.addBreadcrumb({
          category: 'telehealth.bridge',
          message: `DeviceBridge switch failed: ${err}`,
          level: 'error',
        });
      });
    }
  }

  // Start mic test mode - only monitors audio levels without recording.
  const startMicTest = async (): Promise<boolean> => {
    try {
      const permissionGranted = await checkMicrophonePermission();
      if (!permissionGranted) {
        return false;
      }

      micTestLevelMeterRef.current?.stop();
      micTestStreamRef.current?.getTracks().forEach(track => track.stop());

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: selectedMicrophone?.deviceId
          ? { deviceId: { exact: selectedMicrophone.deviceId } }
          : true,
      });
      const levelMeter = new MicLevelMeter();
      levelMeter.setOnLevelChanged(averageLevel => {
        setAudioData(Math.max(0, Math.min(100, averageLevel)));
      });
      levelMeter.start(stream);
      micTestStreamRef.current = stream;
      micTestLevelMeterRef.current = levelMeter;
      setIsMicTesting(true);
      return true;
    } catch (error) {
      console.error('Error starting mic test:', error);
      micTestLevelMeterRef.current?.stop();
      micTestStreamRef.current?.getTracks().forEach(track => track.stop());
      micTestLevelMeterRef.current = null;
      micTestStreamRef.current = null;
      setIsMicTesting(false);
      return false;
    }
  };

  // Stop mic test mode
  const stopMicTest = () => {
    micTestLevelMeterRef.current?.stop();
    micTestStreamRef.current?.getTracks().forEach(track => track.stop());
    micTestLevelMeterRef.current = null;
    micTestStreamRef.current = null;
    setIsMicTesting(false);
    // Don't reset audio data - keep it to show the checkmark.
  };

  // --- UploadService event handlers (use refs to avoid stale closures) ---
  const handleUploadServiceChunkUploaded = useCallback(
    (data: { sessionId: string; chunkIndex: number; s3ObjectId: string }) => {
      const session = currentRecordingSession.current;
      if (!session) return;
      const chunk = session.chunks.find(c => c.position === data.chunkIndex);
      if (chunk) {
        session.updateChunkUploadStatus(chunk.id, data.s3ObjectId, true);
      }
      // Clear any previous S3 error on successful upload
      onS3UploadErrorRef.current?.('');

      // Keep the permanent recovery manifest in sync with uploads.
      if (sessionManifestRef.current) {
        sessionManifestRef.current
          .markChunkUploaded(data.sessionId, data.chunkIndex)
          .catch(() => {});
      }
    },
    [],
  );

  const handleUploadServiceChunkFailed = useCallback(
    (data: { sessionId: string; chunkIndex: number; error: string; willRetry: boolean }) => {
      if (data.willRetry) return; // will be retried, no action needed yet
      const session = currentRecordingSession.current;
      if (!session) return;
      const chunk = session.chunks.find(c => c.position === data.chunkIndex);
      if (chunk) {
        session.updateChunkUploadStatus(chunk.id, undefined, false);
      }
      onS3UploadErrorRef.current?.(data.error);
    },
    [],
  );

  const handleUploadServiceChunkDeadLettered = useCallback(
    (data: { entry: { sessionId: string; chunkIndex: number; error: string } }) => {
      const session = currentRecordingSession.current;
      if (!session) return;
      const chunk = session.chunks.find(c => c.position === data.entry.chunkIndex);
      if (chunk) {
        session.updateChunkUploadStatus(chunk.id, undefined, false);
      }
      onS3UploadErrorRef.current?.(data.entry.error);

      // Keep the permanent recovery manifest in sync with terminal failures.
      if (sessionManifestRef.current) {
        sessionManifestRef.current
          .markChunkFailed(data.entry.sessionId, data.entry.chunkIndex)
          .catch(() => {});
      }
    },
    [],
  );

  const handleUploadServiceNetworkChanged = useCallback(
    (data: { isOnline: boolean }) => {
      if (!data.isOnline) {
        enterLowDataMode();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // --- RecordingService event handlers ---

  const handleRecordingServiceChunkReady = useCallback(
    (data: RecordingEventMap['chunkReady']) => {
      if (!currentRecordingSession.current) {
        SentryService.logEvent('RecordingService chunkReady but no active session', {
          level: 'warning',
          extra: { sessionId: data.sessionId, chunkIndex: data.chunkIndex },
        });
        return;
      }

      try {
        currentRecordingSession.current.addChunkToRecordingSession({
          id: data.chunkId,
          start_timestamp_seconds: Math.max(0, elapsedTime.current - DEFAULT_TIMESLICE_MS / 1000),
          end_timestamp_seconds: elapsedTime.current,
          upload_status: 'pending',
          chunk_data: {
            chunk_size: data.byteSize,
            chunk_type: data.contentType,
          },
          position: data.chunkIndex,
          transcription_session_id: data.sessionId,
          is_silent: data.isSilent,
          peak_level: data.peakLevel,
          health_state: data.healthState,
        });

        // RecordingService already enqueued to UploadService — just track the count
        enqueuedChunkCountRef.current++;

        // Keep the permanent recovery manifest in sync with new chunks.
        if (sessionManifestRef.current) {
          sessionManifestRef.current.incrementTotalChunks(data.sessionId).catch(() => {});
        }
      } catch (error) {
        SentryService.logEvent('RecordingService chunkReady handler error', {
          level: 'error',
          extra: { error, sessionId: data.sessionId, chunkIndex: data.chunkIndex },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleRecordingServiceError = useCallback(
    (data: RecordingEventMap['error']) => {
      SentryService.logEvent('RecordingService error', {
        level: 'error',
        extra: { message: data.message, cause: data.cause },
      });
      enqueueSnackbar(`Recording error: ${data.message}`, {
        variant: 'error',
        preventDuplicate: true,
      });
      recordingTracking.error(data.message, {
        session_id: transcriptionSessionIdRef.current ?? undefined,
        recording_pipeline: 'streaming',
      });
    },
    [enqueueSnackbar, recordingTracking],
  );

  const handleRecordingServiceChunkError = useCallback(
    (data: RecordingEventMap['chunkError']) => {
      SentryService.logEvent('RecordingService chunk error', {
        level: 'error',
        extra: { chunkId: data.chunkId, error: data.error },
      });
      currentRecordingSession.current?.addLog('RecordingService Chunk Error', {
        chunk_id: data.chunkId,
        error: data.error,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleRecordingServiceAudioLevel = useCallback(
    (data: RecordingEventMap['audioLevel']) => {
      // RecordingService now emits an honest 0-100 level (time-domain RMS +
      // per-mic adaptive noise floor), so feed it straight to the waveform.
      setAudioData(Math.max(0, Math.min(100, data.level)));
    },
    [setAudioData],
  );

  // Initialize/dispose the permanent UploadService.
  useEffect(() => {
    const storageService = storageServiceRef.current;
    if (!storageService) {
      disableStreamingPipeline(
        'storage_not_ready',
        new Error('StorageService unavailable while initializing UploadService'),
      );
      return;
    }

    const service = new UploadService({
      apiBaseUrl: API_BASE_URL,
      getAccessToken,
      storageService,
      recordingPipeline: 'streaming',
    });
    uploadServiceRef.current = service;
    setExposedUploadService(service);

    service.on('chunkUploaded', handleUploadServiceChunkUploaded);
    service.on('chunkFailed', handleUploadServiceChunkFailed);
    service.on('chunkDeadLettered', handleUploadServiceChunkDeadLettered);
    service.on('networkChanged', handleUploadServiceNetworkChanged);

    service.start();

    SentryService.addBreadcrumb({
      category: 'upload.service',
      message: 'UploadService initialized',
      level: 'info',
    });

    return () => {
      service.off('chunkUploaded', handleUploadServiceChunkUploaded);
      service.off('chunkFailed', handleUploadServiceChunkFailed);
      service.off('chunkDeadLettered', handleUploadServiceChunkDeadLettered);
      service.off('networkChanged', handleUploadServiceNetworkChanged);
      service.dispose();
      uploadServiceRef.current = null;
      setExposedUploadService(null);
    };
  }, [
    disableStreamingPipeline,
    getAccessToken,
    handleUploadServiceChunkUploaded,
    handleUploadServiceChunkFailed,
    handleUploadServiceChunkDeadLettered,
    handleUploadServiceNetworkChanged,
  ]);

  // Initialize/dispose the permanent RecordingService (requires StorageService + UploadService).
  useEffect(() => {
    if (!storageServiceRef.current || !uploadServiceRef.current) {
      disableStreamingPipeline(
        'recording_start',
        new Error('RecordingService dependencies unavailable during initialization'),
      );
      return;
    }

    const service = new RecordingService({
      storageService: storageServiceRef.current,
      uploadService: uploadServiceRef.current,
      // Health snapshot read at chunk-finalize time (GDN-2204) — ref-based so
      // the provider stays valid regardless of AudioInputDeviceService lifecycle
      getHealthState: () => audioDeviceServiceRef.current?.getHealthState() ?? null,
    });
    recordingServiceRef.current = service;
    setExposedRecordingService(service);

    service.on('chunkReady', handleRecordingServiceChunkReady);
    service.on('error', handleRecordingServiceError);
    service.on('chunkError', handleRecordingServiceChunkError);
    service.on('audioLevel', handleRecordingServiceAudioLevel);

    SentryService.addBreadcrumb({
      category: 'recording.service',
      message: 'RecordingService initialized',
      level: 'info',
    });

    return () => {
      service.off('chunkReady', handleRecordingServiceChunkReady);
      service.off('error', handleRecordingServiceError);
      service.off('chunkError', handleRecordingServiceChunkError);
      service.off('audioLevel', handleRecordingServiceAudioLevel);
      service.dispose();
      recordingServiceRef.current = null;
      setExposedRecordingService(null);
    };
  }, [
    disableStreamingPipeline,
    handleRecordingServiceChunkReady,
    handleRecordingServiceError,
    handleRecordingServiceChunkError,
    handleRecordingServiceAudioLevel,
  ]);

  // Initialize/dispose the permanent SessionCompletionService + SessionRecoveryService (ADR-007).
  // Requires StorageService + UploadService + SessionManifest
  useEffect(() => {
    if (!storageServiceRef.current || !uploadServiceRef.current) {
      disableStreamingPipeline(
        'session_completion',
        new Error('Session completion dependencies unavailable during initialization'),
      );
      return;
    }

    let disposed = false;

    const manifest = new SessionManifest();
    sessionManifestRef.current = manifest;

    const init = async () => {
      await manifest.open();
      if (disposed) {
        await manifest.close();
        return;
      }

      // Wait for StorageService to finish initializing — it may still be
      // in 'initializing' state because the storage effect starts init async.
      const storage = storageServiceRef.current!;
      const storageStatus = storage.getSnapshot().status;
      if (storageStatus !== 'ready') {
        if (storageStatus === 'error') {
          await manifest.close();
          return;
        }
        await new Promise<void>((resolve, reject) => {
          const unsub = storage.subscribe(() => {
            const s = storage.getSnapshot();
            if (s.status === 'ready') {
              unsub();
              resolve();
            } else if (s.status === 'error') {
              unsub();
              reject(new Error(`StorageService failed: ${s.error}`));
            }
          });
        });
      }

      if (disposed) {
        await manifest.close();
        return;
      }

      const completionService = new SessionCompletionService({
        uploadService: uploadServiceRef.current!,
        storageService: storageServiceRef.current!,
        sessionManifest: manifest,
        getAccessToken,
      });
      sessionCompletionServiceRef.current = completionService;
      setExposedCompletionService(completionService);

      const recoveryService = new SessionRecoveryService({
        storageService: storageServiceRef.current!,
        uploadService: uploadServiceRef.current!,
        completionService,
        sessionManifest: manifest,
      });
      sessionRecoveryServiceRef.current = recoveryService;
      setExposedRecoveryService(recoveryService);

      // Reset stale recordingStatus when recovery finds orphaned sessions.
      // After a crash, localStorage still has isRecording: true — fix that.
      recoveryService.on('scanComplete', ({ found, recovered, failed, cleaned }) => {
        if (found > 0) {
          // Don't reset recording status if a recording started after the scan began
          const serviceActive = recordingServiceRef.current?.getSnapshot().status === 'recording';
          if (serviceActive) return;

          setRecordingStatus(prev => ({
            ...prev,
            isRecording: false,
            isStarting: false,
            isStopping: false,
            readyToRecord: true,
          }));
        }
        sessionRecoveryTracking.scanCompleted({ found, recovered, failed, cleaned });
      });

      recoveryService.on('sessionRecovered', ({ result }) => {
        sessionRecoveryTracking.sessionRecovered({
          session_id: result.sessionId,
          total_chunks: result.totalChunks,
          uploaded_chunks: result.uploadedChunks,
          failed_chunks: result.failedChunks,
        });
      });

      recoveryService.on('sessionFailed', ({ sessionId, error }) => {
        sessionRecoveryTracking.sessionFailed({ session_id: sessionId, error });
      });

      recoveryService.on('staleManifestCleaned', ({ sessionId, reason, totalChunks }) => {
        sessionRecoveryTracking.staleManifestCleaned({
          session_id: sessionId,
          reason,
          total_chunks: totalChunks,
        });
      });

      SentryService.addBreadcrumb({
        category: 'session.completion',
        message: 'SessionCompletionService + SessionRecoveryService initialized',
        level: 'info',
      });

      // Log if the browser discarded the previous page (tab was killed after hours backgrounded)
      if ((document as { wasDiscarded?: boolean }).wasDiscarded) {
        SentryService.addBreadcrumb({
          category: 'recording.lifecycle',
          message: 'Previous page was discarded by browser',
          level: 'error',
        });
      }

      // Auto-scan for orphaned sessions on initialization
      recoveryService.scan().catch(error => {
        SentryService.captureException(error instanceof Error ? error : new Error(String(error)));
      });
    };

    init().catch(error => {
      SentryService.captureException(
        error instanceof Error
          ? error
          : new Error(`Session services init failed: ${String(error)}`),
      );
      disableStreamingPipeline('session_completion', error);
    });

    return () => {
      disposed = true;
      sessionRecoveryServiceRef.current?.dispose();
      sessionRecoveryServiceRef.current = null;
      setExposedRecoveryService(null);
      sessionCompletionServiceRef.current?.dispose();
      sessionCompletionServiceRef.current = null;
      setExposedCompletionService(null);
      manifest.close().catch(() => {});
      sessionManifestRef.current = null;
    };
  }, [disableStreamingPipeline, getAccessToken]);

  /**
   * Build the audio-health summary for the end-session payload (GDN-2205) and
   * emit the queryable Sentry event for silent/failed sessions — the ambient
   * scope (device profile, tags) rides along with the event.
   */
  const collectAudioHealthSummary = (): AudioHealthSummary | undefined => {
    const session = currentRecordingSession.current;
    if (!session) return undefined;

    const summary = session.getAudioHealthSummary(
      audioDeviceServiceRef.current?.getHealthState() ?? null,
    );

    if (summary.silent_chunk_count > 0 || summary.final_health_state === 'failed') {
      SentryService.logEvent('Recording session ended with silent/failed audio', {
        level: 'error',
        tags: { audio_health: summary.final_health_state ?? 'unknown' },
        extra: {
          transcriptionSessionId: session.transcription_session_id,
          ...summary,
        },
      });
    }

    return summary;
  };

  // Internal function to perform the actual stop recording logic
  // This is called by both initiateEndRecording (with guard) and requestRemoteStop (without guard)
  const performStopRecording = async (): Promise<boolean> => {
    try {
      const shortRecording = elapsedTime.current < 5;

      isRecordingEnding.current = true;
      SentryService.setTag('recordingPhase', 'stopping');
      setAudioData(0);
      setRecordingStatus(prev => {
        const newStatus = {
          ...prev,
          isRecording: false,
          isStopping: true,
        };
        sendMessageToExtension({ type: 'recordingStatus', data: newStatus });
        return newStatus;
      });

      // ============================================================================
      // CRITICAL: Capture session ID BEFORE stopping the recorder
      // ============================================================================
      // The stop() call yields to microtask queues and can allow state/refs to be
      // cleared (especially during tab takeover). Capture the ID while everything
      // is still alive.
      //
      // Fallback chain (in order of reliability):
      // 1. Ref - stable reference set in performStartRecording, not affected by closures
      // 2. localStorage direct read - avoids React state/closure staleness
      // 3. React state - may be stale in useCallback contexts
      // 4. RecordingSession object - last resort, may also be cleared
      // ============================================================================
      let effectiveSessionId = transcriptionSessionIdRef.current;

      if (!effectiveSessionId) {
        try {
          const storedSessionId = localStorage.getItem('transcriptionSessionId');
          if (storedSessionId) {
            effectiveSessionId = JSON.parse(storedSessionId);
          }
        } catch (e) {
          console.warn('Failed to parse transcriptionSessionId from localStorage:', e);
        }
      }

      if (!effectiveSessionId && currentTranscriptionSessionId) {
        effectiveSessionId = currentTranscriptionSessionId;
      }

      if (!effectiveSessionId && currentRecordingSession.current?.transcription_session_id) {
        effectiveSessionId = currentRecordingSession.current.transcription_session_id;
      }

      // RecordingService flushes final data, finalizes remaining chunks, and stops MediaRecorder.
      // CRITICAL: stop() MUST complete before mixer cleanup — closing AudioContext before
      // MediaRecorder.stop() corrupts the final chunk.
      if (!recordingServiceRef.current) {
        throw new Error('RecordingService unavailable while stopping');
      }
      await recordingServiceRef.current.stop();
      streamCleanupRef.current?.();
      streamCleanupRef.current = null;
      mixerResultRef.current = null;

      recordingTracking.stop({
        session_id: effectiveSessionId ?? transcriptionSessionIdRef.current ?? undefined,
        recording_pipeline: 'streaming',
        duration_seconds: elapsedTime.current,
      });

      // NOTE: We intentionally do NOT release the AudioInputDeviceService stream here.
      // The stream stays alive so the level meter continues showing mic activity between
      // recordings (waiting room pattern). Release only happens on page navigation,
      // session cleanup, or tab visibility change.

      if (!effectiveSessionId) {
        const localStorageRaw = localStorage.getItem('transcriptionSessionId');
        console.error('No transcription session ID found after all fallbacks.', {
          reactState: currentTranscriptionSessionId,
          ref: transcriptionSessionIdRef.current,
          localStorage: localStorageRaw,
          recordingSession: currentRecordingSession.current?.transcription_session_id,
        });
        SentryService.logEvent('No transcription session ID found - all fallbacks failed', {
          level: 'error',
          extra: {
            reactState: currentTranscriptionSessionId,
            refValue: transcriptionSessionIdRef.current,
            localStorageRaw,
            hasRecordingSession: !!currentRecordingSession.current,
            pipeline: 'streaming',
            elapsedTime: elapsedTime.current,
            recordingServiceStatus: recordingServiceRef.current ? 'exists' : 'null',
          },
        });
        // NOTE: We intentionally do NOT call cleanUpSession() here anymore.
        // Previously, calling cleanUpSession() would delete the recording data from IndexedDB,
        // causing the upload to fail with "Chunk not found in IndexedDB" error.
        // By not cleaning up, we preserve the data and allow retry mechanisms to work.
        sessionCompletionTracking.fatalError({
          session_id: undefined,
          error_type: 'no_session_id',
          duration_seconds: elapsedTime.current,
        });
        return false;
      }

      currentRecordingSession.current?.addMediaStreamInfo({
        streamEndedAt: new Date().toISOString(),
      });

      if (shortRecording) {
        enqueueSnackbar(
          'This recording is too short to be processed. If you are just testing JotPsych, please submit a recording at least a minute in length.',
          { variant: 'error' },
        );
        SentryService.logEvent('Short Recording', {
          level: 'info',
          extra: { elapsedTime: elapsedTime.current },
        });
        recordingTracking.shortDismissed({
          session_id: transcriptionSessionIdRef.current ?? undefined,
          encounter_id: recordingEncounterIdRef.current ?? undefined,
          duration_seconds: elapsedTime.current,
          recording_pipeline: 'streaming',
        });

        // Track as a completion event so every recording_started has a corresponding terminal event
        sessionCompletionTracking.finished({
          session_id: effectiveSessionId,
          total_chunks: 0,
          uploaded_chunks: 0,
          failed_chunks: 0,
          failed_chunks_ratio: 0,
          drain_duration_seconds: 0,
          timed_out: false,
          recording_end_confirmed: false,
          status: 'short_recording_discarded',
        });

        cleanUpSession();
        return true;
      }

      const completionService = sessionCompletionServiceRef.current;
      if (!completionService) {
        throw new Error('SessionCompletionService unavailable while stopping recording');
      }
      const totalChunks = enqueuedChunkCountRef.current;
      const capturedSessionId = effectiveSessionId;

      // Transition to "stopped" immediately while permanent completion drains in the background.
      setRecordingStatus(prev => ({
        ...prev,
        isStopping: false,
        readyToSubmit: true,
      }));

      // Invoke onRecordingStopped callback — creates the recording document BEFORE
      // any caller (e.g. TelehealthCallManager) proceeds with cleanup.
      if (onRecordingStoppedRef.current) {
        try {
          await onRecordingStoppedRef.current(capturedSessionId, elapsedTime.current);
        } catch (error) {
          console.error('Error in onRecordingStopped callback:', error);
          SentryService.logEvent('Error in onRecordingStopped callback', {
            level: 'error',
            extra: { error, transcriptionSessionId: capturedSessionId },
          });
        }
      }

      // Fire-and-forget: drain + recording-end run in background.
      // Storage + manifest cleanup is handled inside completeSession() (before status transition).
      const recordingSessionLogs = currentRecordingSession.current?.generateLogsString() || '';
      const audioHealthSummary = collectAudioHealthSummary();
      completionService
        .startCompletion(capturedSessionId, totalChunks, recordingSessionLogs, audioHealthSummary)
        .then(async result => {
          // Call the finalized callback if set.
          if (onRecordingFinalizedRef.current) {
            try {
              await onRecordingFinalizedRef.current(capturedSessionId, elapsedTime.current);
            } catch (error) {
              console.error('Error in onRecordingFinalized callback:', error);
              SentryService.logEvent('Error in onRecordingFinalized callback', {
                level: 'error',
                extra: { error, transcriptionSessionId: capturedSessionId },
              });
            }
          }

          SentryService.setTag('recordingPhase', 'completed');
          SentryService.addBreadcrumb({
            category: 'session.completion',
            message: `Completion done: uploaded=${result.uploadedChunks}, failed=${result.failedChunks}, timedOut=${result.timedOut}`,
            level: 'info',
          });

          // Analytics: drain phase completed
          sessionCompletionTracking.drainCompleted({
            session_id: capturedSessionId,
            drain_duration_ms: result.drainDurationMs,
            completed_count: result.uploadedChunks,
            failed_count: result.failedChunks,
            timed_out: result.timedOut,
          });

          // Analytics: session completion finished
          sessionCompletionTracking.finished({
            session_id: capturedSessionId,
            total_chunks: result.totalChunks,
            uploaded_chunks: result.uploadedChunks,
            failed_chunks: result.failedChunks,
            failed_chunks_ratio:
              result.totalChunks > 0 ? result.failedChunks / result.totalChunks : 0,
            drain_duration_seconds: result.drainDurationMs / 1000,
            timed_out: result.timedOut,
            recording_end_confirmed: result.recordingEndConfirmed,
            status: result.failedChunks > 0 ? 'completed_with_losses' : 'completed',
          });

          // Analytics: timeout event (separate for easy dashboard filtering)
          if (result.timedOut) {
            sessionCompletionTracking.timeout({
              session_id: capturedSessionId,
              queued_at_timeout: uploadServiceRef.current?.getSnapshot().queueDepth,
              in_flight_at_timeout: uploadServiceRef.current?.getSnapshot().inFlightCount,
            });
          }
        })
        .catch(error => {
          console.error('SessionCompletionService.startCompletion failed:', error);
          SentryService.captureException(error instanceof Error ? error : new Error(String(error)));

          // Analytics: network failure during completion
          sessionCompletionTracking.networkFailure({
            session_id: capturedSessionId,
            pending_chunks: uploadServiceRef.current?.getSnapshot().queueDepth,
          });
          sessionCompletionTracking.fatalError({
            session_id: capturedSessionId,
            error_type: 'completion_failed',
            error_message: (error instanceof Error ? error.message : String(error)).slice(0, 200),
            pending_chunks: uploadServiceRef.current?.getSnapshot().queueDepth,
            duration_seconds: elapsedTime.current,
          });
        });
      return true;
    } catch (error) {
      console.error('Error initiating end recording', error);
      SentryService.logEvent('Error initiating end recording.', {
        level: 'error',
        extra: { error, transcriptionSessionId: currentTranscriptionSessionId },
      });
      sessionCompletionTracking.fatalError({
        session_id: transcriptionSessionIdRef.current || undefined,
        error_type: 'stop_failed',
        error_message: (error instanceof Error ? error.message : String(error)).slice(0, 200),
        duration_seconds: elapsedTime.current,
      });
      return false;
    }
  };

  // Keep ref in sync so requestRemoteStop (stale useCallback) always calls the latest version
  performStopRecordingRef.current = performStopRecording;

  // Update initiateEndRecording method to use tabCommunicator
  const initiateEndRecording = async (): Promise<boolean> => {
    // Only allow the owner tab to end the recording
    if (!allowRecorderControl) {
      enqueueSnackbar('Only the tab that started the recording can control it', {
        variant: 'warning',
      });
      return false;
    }

    return performStopRecording();
  };

  // Initiate a new recording session - Update this method to use the tabCommunicator
  const initiateRecording = async (): Promise<boolean> => {
    // GDN-2784: the encounter surface may require a user confirmation before
    // any recording starts (e.g. the encounter is scheduled for a future
    // day). A decline aborts quietly — callers handle the false return.
    while (beforeRecordingStartRef.current) {
      const gate = beforeRecordingStartRef.current;
      const gateKey = beforeRecordingStartKeyRef.current;
      const inFlight = beforeStartGateInFlightRef.current;
      if (inFlight) {
        // Re-entry latch: the gate can legitimately take seconds (waiting
        // for the encounter to load, or for the user to answer the
        // confirmation). A duplicate attempt under the same registration is
        // declined rather than queued — the first invocation owns the gate.
        if (inFlight.key === gateKey) return false;
        // A different key means the holder belongs to a superseded
        // registration (the view navigated); it is guaranteed to fail
        // closed within one poll tick. Park behind it rather than dropping
        // — this attempt (e.g. a telehealth auto-start, which never
        // retries) is the legitimate start for the CURRENT encounter.
        await inFlight.promise.catch(() => false);
        continue;
      }
      setBeforeStartGatePending(true);
      try {
        const gateRun = gate();
        beforeStartGateInFlightRef.current = { key: gateKey, promise: gateRun };
        const proceed = await gateRun;
        if (!proceed) return false;
      } finally {
        beforeStartGateInFlightRef.current = null;
        setBeforeStartGatePending(false);
      }
      break;
    }
    try {
      // Tag all recording errors for Sentry filtering — set early so pre-recording
      // checks (storage, persistence) are also captured under this tag
      SentryService.setTag('recordingType', 'encounter');
      SentryService.setTag('recordingPhase', 'pre_check');

      if (!recordingStatus.readyToRecord) {
        console.log("Wasn't ready to record, resetting");
        throw new Error('Not ready to record');
      }

      const storageService = storageServiceRef.current;
      const storageState = storageService?.getSnapshot();
      if (!storageService || storageState?.status !== 'ready') {
        disableStreamingPipeline(
          'storage_not_ready',
          new Error(
            `StorageService not ready at recording start (status: ${storageState?.status ?? 'unavailable'})`,
          ),
        );
        return false;
      }

      if (!uploadServiceRef.current) {
        disableStreamingPipeline(
          'recording_start',
          new Error('UploadService unavailable at recording start'),
        );
        return false;
      }

      if (!recordingServiceRef.current) {
        disableStreamingPipeline(
          'recording_start',
          new Error('RecordingService unavailable at recording start'),
        );
        return false;
      }

      const completionService = sessionCompletionServiceRef.current;
      const sessionManifest = sessionManifestRef.current;
      if (!completionService || !sessionManifest) {
        disableStreamingPipeline(
          'session_completion',
          new Error('Session completion services unavailable at recording start'),
        );
        return false;
      }

      // Re-request persistence before recording. A denied grant is non-fatal
      // because StorageService still owns the selected OPFS/IndexedDB backend.
      const persisted = await storageService.requestPersistence();
      const estimate = await storageService.getStorageEstimate();
      if (estimate) {
        SentryService.addBreadcrumb({
          category: 'recording',
          message: `Pre-recording storage: ${Math.round(estimate.usageBytes / (1024 * 1024))}MB used / ${Math.round(estimate.quotaBytes / (1024 * 1024))}MB quota, persisted=${persisted}`,
          level: 'info',
        });

        const estimatedChunkSizeMB = 5 * (RECORDING_CHUNK_LENGTH_SECONDS / 60);
        const minimumRequiredQuotaMB = estimatedChunkSizeMB;
        const availableBytes = estimate.quotaBytes - estimate.usageBytes;
        const minimumRequiredBytes = minimumRequiredQuotaMB * 1024 * 1024;

        if (availableBytes < minimumRequiredBytes) {
          const availableMB = Math.round(availableBytes / (1024 * 1024));
          const quotaMB = Math.round(estimate.quotaBytes / (1024 * 1024));
          const usagePercent = Math.round((estimate.usageBytes / estimate.quotaBytes) * 100);

          setStorageInfo({ availableMB, quotaMB, usagePercent, minimumRequiredQuotaMB });

          const errorMessage = `Not enough storage space available (${availableMB}MB free of ${quotaMB}MB, ${usagePercent}% used). Please free up storage space and try again.`;
          console.error(errorMessage);
          SentryService.logEvent('Insufficient storage space for recording', {
            level: 'error',
            extra: { availableMB, quotaMB, usagePercent, minimumRequiredQuotaMB },
          });

          enqueueSnackbar(
            <div className="flex flex-col gap-4 lg:flex-row">
              <div>{errorMessage}</div>
              <Button
                onClick={() => setShowStorageCleanupDialog(true)}
                variant="contained"
                sx={{
                  flexShrink: 0,
                  maxHeight: '42px',
                  bgcolor: 'background.paper',
                  color: 'error.main',
                  '&:hover': {
                    opacity: 0.9,
                    bgcolor: 'background.paper',
                  },
                  transition: 'opacity 0.2s ease-in-out',
                }}
              >
                Manage Storage
              </Button>
            </div>,
            { variant: 'error' },
          );
          SentryService.setTag('recordingType', '');
          SentryService.setTag('recordingPhase', '');
          return false;
        }
      }

      // Reset UploadService refs for new recording
      enqueuedChunkCountRef.current = 0;

      // Reset SessionCompletionService state (ensures canGenerateNote is false for new session)
      completionService.reset();

      const startingTimestamp = Date.now();

      SentryService.setTag('recordingPhase', 'initialization');
      setRecordingStatus(prev => ({
        ...prev,
        isStarting: true,
      }));

      const newRecordingSession = new RecordingSession({});
      const recordingId = newRecordingSession.id;

      SentryService.setTag('recordingId', recordingId);

      localStorage.setItem('currentRecordingSessionId', newRecordingSession.id);
      newRecordingSession.startSession();
      newRecordingSession.addLog('DeviceInfo', {
        platform: 'web-recorder',
        version: FRONTEND_RECORDING_APP_VERSION,
      });

      // Add initial media stream info
      newRecordingSession.addMediaStreamInfo({
        useSharedMedia,
        selectedMicrophoneId: selectedMicrophone?.deviceId,
        streamCreatedAt: new Date().toISOString(),
      });

      currentRecordingSession.current = newRecordingSession;

      // Create breadcrumb for transcription session start
      SentryService.addBreadcrumb({
        category: 'audio.session',
        message: 'Starting new transcription session',
        level: 'info',
        data: {
          recordingId,
          useSharedMedia,
          selectedMicrophoneId: selectedMicrophone?.deviceId,
        },
      });

      const newTranscriptionSessionId = await startTranscriptionSession(
        newRecordingSession.id,
        await getAccessToken(),
        'streaming',
      );

      if (!newTranscriptionSessionId) {
        console.error("Couldn't get a new transcription session id.");
        SentryService.addBreadcrumb({
          category: 'audio.session',
          message: 'Failed to get transcription session ID',
          level: 'error',
        });
        throw new Error('Failed to get transcription session ID');
      }

      // Reset elapsed time if new transcription session
      elapsedTime.current = 0;
      setElapsedSeconds(0);
      // Store actual recording start time
      recordingStartTime.current = Date.now();

      setCurrentTranscriptionSessionId(newTranscriptionSessionId);
      // Also store in ref as fallback for stale closure issues (see ref declaration for details)
      transcriptionSessionIdRef.current = newTranscriptionSessionId;
      newRecordingSession.associateTranscriptionSession(newTranscriptionSessionId);

      // Set transcription session ID as a tag for tracing
      SentryService.setTag('transcriptionSessionId', newTranscriptionSessionId);
      SentryService.setTransactionAttribute('transcriptionSessionId', newTranscriptionSessionId);

      // Acquire stream via the permanent AudioInputDeviceService.
      let preAcquiredStream: MediaStream | undefined;
      try {
        const service = audioDeviceServiceRef.current;
        if (!service?.isInitialized()) {
          throw new Error('AudioInputDeviceService is not initialized');
        }
        const existingStream = service.getActiveStream();
        const devices = service.getDevices();
        const target = selectedMicrophone
          ? devices.find(d => d.browserDeviceId === selectedMicrophone.deviceId)
          : undefined;

        // Check audio health state — a stream can report active/live tracks
        // while producing zero audio (e.g. after Bluetooth disconnect, OS
        // interruption, or Chrome resource reclaim). The health monitor
        // detects this via AnalyserNode RMS analysis.
        const healthState = service.getHealthState();
        const isHealthy = healthState === 'healthy' || healthState === 'initializing';

        // A pre-acquired stream (health monitoring / mic test) is captured
        // raw, so it can only be reused when this recording wants raw too —
        // telehealth needs an AEC-enabled capture.
        const acquireConstraints = getRecordingAcquireConstraints();
        const constraintsMatch =
          !acquireConstraints?.echoCancellation ||
          existingStream?.stream.getAudioTracks()[0]?.getSettings().echoCancellation === true;

        if (
          existingStream &&
          existingStream.stream.active &&
          existingStream.stream.getAudioTracks().some(t => t.readyState === 'live') &&
          (!target || existingStream.device.internalId === target.internalId) &&
          isHealthy &&
          constraintsMatch
        ) {
          // Reuse the pre-acquired stream (from health monitoring)
          preAcquiredStream = existingStream.stream;
          newRecordingSession.addLog('AudioServiceReusedPreAcquired', {
            deviceId: existingStream.device.browserDeviceId,
            transport: existingStream.device.transport,
            healthState,
          });
        } else {
          // Different device, stale stream, or unhealthy audio — acquire fresh
          if (!isHealthy && existingStream) {
            newRecordingSession.addLog('AudioServiceStaleStreamRejected', {
              healthState,
              streamActive: existingStream.stream.active,
              trackReadyState: existingStream.stream.getAudioTracks()[0]?.readyState,
            });
          }
          const acquireResult = await service.acquire(target?.internalId, acquireConstraints);
          preAcquiredStream = acquireResult.stream;

          if (acquireResult.wasSubstituted) {
            audioDeviceTracking.substituted({
              requested_transport: target?.transport,
              actual_transport: acquireResult.device.transport,
            });
            setSelectedMicrophone(normalizedToMediaDeviceInfo(acquireResult.device));

            // Notify user about the device switch
            const requestedLabel = selectedMicrophone?.label || 'Selected microphone';
            const actualLabel = acquireResult.device.displayLabel || 'default microphone';
            enqueueSnackbar(`${requestedLabel} unavailable. Recording with ${actualLabel}.`, {
              variant: 'warning',
            });
          }

          newRecordingSession.addLog('AudioServiceAcquired', {
            deviceId: acquireResult.device.browserDeviceId,
            wasSubstituted: acquireResult.wasSubstituted,
            transport: acquireResult.device.transport,
            echoCancellation: acquireConstraints?.echoCancellation ?? false,
          });
        }
      } catch (error) {
        console.error('AudioInputDeviceService acquire failed, falling back', error);
        SentryService.captureException(error instanceof Error ? error : new Error(String(error)), {
          tags: { component: 'RecordingContext', method: 'initiateRecording' },
        });
        // Preserve the launched path's resilience: RecordingService can still
        // acquire a raw stream directly when the device service fails.
      }

      if (!preAcquiredStream) {
        // Fallback: acquire stream directly when AudioInputDeviceService is not active.
        preAcquiredStream = await navigator.mediaDevices.getUserMedia({
          audio: selectedMicrophone?.deviceId
            ? { deviceId: { exact: selectedMicrophone.deviceId } }
            : true,
        });
      }

      // Telehealth/headphones: mix mic + system audio if shared media is enabled.
      const mixerResult = await acquireRecordingStream(preAcquiredStream, {
        useSharedMedia,
        preferCurrentTab: isLoadedInChromeExtension.current,
      });
      streamCleanupRef.current = mixerResult.cleanup;
      mixerResultRef.current = mixerResult;

      // Phase 3c/4c: Wire telehealth remote audio into mixer (only when session configured).
      if (dailyCallServiceRef.current && telehealthConfigRef.current) {
        const rawMicTrack = preAcquiredStream.getAudioTracks()[0];

        // 1. Acquire an echo-cancelled call track for Daily. The recording
        //    keeps the raw mic; the call must NOT transmit it (no AEC means
        //    the patient hears their own voice echoed back).
        const deviceBridge = new DeviceBridgeService(dailyCallServiceRef.current);
        deviceBridgeRef.current = deviceBridge;
        const dailyMicTrack = await deviceBridge.createInitialCallTrack(rawMicTrack);

        // 2. Push call mic to Daily
        if (dailyCallServiceRef.current.getCallObject()) {
          // Phase 4c: call already initialized at join-session time
          await dailyCallServiceRef.current.setAudioDevice(dailyMicTrack);
        } else {
          // Legacy: initialize at recording start
          dailyCallServiceRef.current.initialize({ audioSource: dailyMicTrack });
        }

        // 3. Subscribe to remote track events
        dailyEventCleanupRef.current = wireDailyTrackEvents(
          dailyCallServiceRef.current,
          mixerResultRef,
        );

        // 4. Join if not yet connected
        const currentState = dailyCallServiceRef.current.getSnapshot();
        if (currentState.status !== 'connected' && currentState.status !== 'joining') {
          dailyCallServiceRef.current
            .join(telehealthConfigRef.current.roomUrl, telehealthConfigRef.current.token)
            .catch(err => {
              SentryService.captureException(err instanceof Error ? err : new Error(String(err)));
              SentryService.addBreadcrumb({
                category: 'telehealth.mixer',
                message: `Daily join failed: ${err}`,
                level: 'error',
              });
            });
        }

        // 5. Sync any existing remote tracks (late-join / already-connected scenario)
        const existingRemoteTracks = dailyCallServiceRef.current.getRemoteAudioTracks();
        existingRemoteTracks.forEach((track, participantId) => {
          mixerResultRef.current?.connectRemoteTrack(participantId, track);
        });

        SentryService.setTag('telehealth_active', 'true');
      }

      // Reset headphones toggle if shared media wasn't actually acquired
      // (user cancelled display picker or no audio track available).
      if (useSharedMedia && !mixerResult.sharedMediaActive) {
        setUseSharedMedia(false);
      }

      const deviceId = preAcquiredStream.getAudioTracks()[0]?.getSettings()?.deviceId;
      await recordingServiceRef.current.start(mixerResult.stream, newTranscriptionSessionId, {
        deviceId,
        mixerAudioContext: mixerResult.audioContext ?? undefined,
        rawMicStream: preAcquiredStream,
      });

      // Create the permanent crash-recovery manifest.
      const mimeType =
        recordingServiceRef.current.getSnapshot().mimeType ?? 'audio/webm;codecs=opus';
      sessionManifest
        .create(newTranscriptionSessionId, mimeType, recordingEncounterIdRef.current ?? undefined)
        .catch(error => {
          SentryService.addBreadcrumb({
            category: 'session.manifest',
            message: `Failed to create manifest: ${error}`,
            level: 'warning',
          });
        });

      // Claim ownership of this recording session
      console.log('Claiming ownership of recording session');
      localStorage.setItem('recording_starter_id', tabCommunicator.getTabId());
      setAllowRecorderControl(true);

      SentryService.setTag('recordingPhase', 'active');
      setRecordingStatus(prev => {
        const newStatus = {
          ...prev,
          isStarting: false,
          isRecording: true,
          readyToRecord: false,
          readyToSubmit: true,
          recordingPaused: false,
        };
        sendMessageToExtension({ type: 'recordingStatus', data: newStatus });
        return newStatus;
      });

      recordingTracking.start({
        session_id: newTranscriptionSessionId,
        recording_pipeline: 'streaming',
        encounter_id: recordingEncounterIdRef.current ?? undefined,
      });
      recordingTracking.startedSuccessfully({
        recording_pipeline: 'streaming',
        time_in_starting_ms: String(Date.now() - startingTimestamp),
      });

      // Start timer and track recording metrics
      startTimer();

      // Ensure audio history updates are not paused
      setPaused(false);

      return true;
    } catch (error) {
      console.error('Error initiating recording:', error);

      // CRITICAL: Clean up the permanent recording mechanism to prevent stale callbacks.
      if (recordingServiceRef.current) {
        try {
          await recordingServiceRef.current.stop();
        } catch {
          // Best-effort — service may already be in error/idle state
        }
        streamCleanupRef.current?.();
        streamCleanupRef.current = null;
        mixerResultRef.current = null;
        // Phase 3c: Clean up telehealth resources on init failure
        dailyEventCleanupRef.current?.();
        dailyEventCleanupRef.current = null;
        deviceBridgeRef.current?.dispose();
        deviceBridgeRef.current = null;
        // Don't destroy dailyCallService — let caller decide
      }

      // Clear any partial session state
      if (
        currentRecordingSession.current &&
        !currentRecordingSession.current.transcription_session_id
      ) {
        console.log('Cleaning up partial recording session');
        currentRecordingSession.current = undefined;
      }

      // Track recording start failure for Statsig observability
      const errorMessage = error instanceof Error ? error.message : String(error);
      recordingTracking.startFailed({
        session_id: transcriptionSessionIdRef.current || undefined,
        error_type: classifyStartError(error),
        error_message: errorMessage.slice(0, 200),
        recording_pipeline: 'streaming',
        had_api_session: !!transcriptionSessionIdRef.current,
        encounter_id: recordingEncounterIdRef.current ?? undefined,
      });

      SentryService.logEvent('Error initiating recording.', {
        level: 'error',
        extra: { error },
      });
      enqueueSnackbar('Failed to start recording. Check audio settings and try again.', {
        variant: 'error',
      });
      await cleanUpSession();
      return false;
    }
  };

  // Clean up the recording session
  const cleanUpSession = async () => {
    console.log('Cleaning up session');

    // Release shared media resources (telehealth/headphones mode)
    streamCleanupRef.current?.();
    streamCleanupRef.current = null;
    mixerResultRef.current = null;

    // Phase 3c: Clean up telehealth event subscriptions (recording stops, call may continue)
    dailyEventCleanupRef.current?.();
    dailyEventCleanupRef.current = null;
    // Do NOT call dailyCallService.leave()/destroy() — call continues after recording stops
    // Do NOT clear dailyCallServiceRef — service outlives recording

    // NOTE: We intentionally do NOT release the AudioInputDeviceService stream here.
    // The stream stays alive so the level meter continues showing mic activity between
    // recordings (waiting room pattern). Release only happens on page navigation,
    // tab visibility change, or unmount.

    // Abort any in-flight uploads and reset UploadService state.
    uploadServiceRef.current?.abortAll();
    enqueuedChunkCountRef.current = 0;

    // Clear chunk storage and session manifest.
    const sessionId = currentRecordingSession.current?.transcription_session_id;
    if (sessionId) {
      const storageService = storageServiceRef.current;
      if (!storageService) {
        throw new Error('StorageService unavailable during session cleanup');
      }
      await storageService.deleteSession(sessionId);
      // Delete manifest to prevent false recovery on reload (e.g. short recordings)
      await sessionManifestRef.current?.delete(sessionId);
    }
    currentRecordingSession.current?.addLog('cleaning up session');

    resetTimer();

    // Clear recording starter ID from localStorage AFTER all other cleanup is done
    localStorage.removeItem('recording_starter_id');
    localStorage.removeItem('currentRecordingSessionId');
    localStorage.removeItem('isLeaving');

    // return recording status state
    setRecordingStatus(prevStatus => ({
      ...prevStatus,
      isRecording: false,
      readyToRecord: true,
      readyToSubmit: false,
      isStopping: false,
      isStarting: false,
      chunksToBeUploaded: false,
      recordingPaused: false,
      isReuploading: false,
    }));

    setLowDataMode(false);

    isRecordingEnding.current = false;
    currentRecordingSession.current = undefined;
    // Clear the transcription session ID ref AND localStorage-backed state
    // to prevent stale session IDs from bleeding into the next encounter
    transcriptionSessionIdRef.current = '';
    setCurrentTranscriptionSessionId('');

    setNoteInformation({
      templateId: templatesList ? templatesList[0]?.template_id : '',
    });

    // clear audio visualizers
    setAudioData(-1);

    // Clear recording Sentry tags to prevent pollution on non-recording errors
    SentryService.setTag('recordingType', '');
    SentryService.setTag('recordingId', '');
    SentryService.setTag('recordingPhase', '');
    SentryService.setTag('transcriptionSessionId', '');

    // Observability: capture expected final state after cleanup
    const finalStatus = {
      ...recordingStatus,
      isRecording: false,
      readyToRecord: true,
      readyToSubmit: false,
      isStopping: false,
      isStarting: false,
      chunksToBeUploaded: false,
    };
    recordingTracking.cleanupCompleted({
      cleanup_type: 'full',
      recording_pipeline: 'streaming',
      final_status_snapshot: JSON.stringify(finalStatus),
      recording_paused_reset: String(finalStatus.recordingPaused === false),
      is_reuploading_reset: String(finalStatus.isReuploading === false),
    });
  };

  // Soft reset for Save & Exit navigation — resets UI/localStorage state so the next
  // encounter starts clean, WITHOUT aborting background drain or deleting storage.
  const softResetForNavigation = () => {
    resetTimer();

    localStorage.removeItem('recording_starter_id');
    localStorage.removeItem('currentRecordingSessionId');
    localStorage.removeItem('isLeaving');

    setRecordingStatus(prevStatus => ({
      ...prevStatus,
      isRecording: false,
      readyToRecord: true,
      readyToSubmit: false,
      isStopping: false,
      isStarting: false,
      chunksToBeUploaded: false,
      recordingPaused: false,
      isReuploading: false,
    }));

    setLowDataMode(false);

    isRecordingEnding.current = false;
    currentRecordingSession.current = undefined;
    // Clear the transcription session ID ref AND localStorage-backed state
    // to prevent stale session IDs from bleeding into the next encounter
    transcriptionSessionIdRef.current = '';
    setCurrentTranscriptionSessionId('');

    setAudioData(-1);

    SentryService.setTag('recordingType', '');
    SentryService.setTag('recordingId', '');
    SentryService.setTag('recordingPhase', '');
    SentryService.setTag('transcriptionSessionId', '');

    // Observability: capture expected final state after soft reset
    const finalStatus = {
      ...recordingStatus,
      isRecording: false,
      readyToRecord: true,
      readyToSubmit: false,
      isStopping: false,
      isStarting: false,
      chunksToBeUploaded: false,
    };
    recordingTracking.cleanupCompleted({
      cleanup_type: 'soft_navigation',
      recording_pipeline: 'streaming',
      final_status_snapshot: JSON.stringify(finalStatus),
      recording_paused_reset: String(finalStatus.recordingPaused === false),
      is_reuploading_reset: String(finalStatus.isReuploading === false),
    });
  };

  // Lightweight reset for crash/dead-button recovery — only touches persisted state.
  // Does NOT tear down services, streams, or timers (none exist after a crash).
  const resetRecordingState = useCallback(() => {
    setRecordingStatus({
      isRecording: false,
      isReuploading: false,
      isStopping: false,
      isStarting: false,
      chunksToBeUploaded: false,
      recordingPaused: false,
      readyToRecord: true,
      readyToSubmit: false,
    });
    localStorage.removeItem('recording_starter_id');
    localStorage.removeItem('currentRecordingSessionId');
    localStorage.removeItem('isLeaving');
    localStorage.removeItem('elapsedTime');
    // Clear stale transcription session ID to prevent cross-encounter bleed
    transcriptionSessionIdRef.current = '';
    setCurrentTranscriptionSessionId('');
  }, [setRecordingStatus, setCurrentTranscriptionSessionId]);

  // Helper function to delete a recording session
  const deleteRecordingHelper = async (): Promise<boolean> => {
    try {
      // Attempt to delete the transcription session
      const response = await APIService.makeAPIPostRequest({
        requestString: '/transcriptionSession/deleteTranscriptionSession',
        accessToken: await getAccessToken(),
        body: { transcription_session_id: currentTranscriptionSessionId },
      });

      if (!response.ok) {
        throw new Error('Failed to delete transcription session.');
      }

      // Clean up recording starter ID
      localStorage.removeItem('recording_starter_id');

      await cleanUpSession(); // call cleanup

      return true;
    } catch (error) {
      console.error('Error deleting recording:', error);
      SentryService.logEvent('Error deleting recording.', {
        level: 'error',
        extra: { error, transcriptionSessionId: currentTranscriptionSessionId },
      });
      return false;
    }
  };

  // Function to send the recording
  const sendRecording = async (): Promise<{ note_id: string; thread_id?: string } | undefined> => {
    if (!currentTranscriptionSessionId) {
      console.error('No transcription session to submit.');
      return undefined;
    }
    try {
      const accessToken = await getAccessToken();

      if (!accessToken) {
        console.error('No access token available.');
        return undefined;
      }
      if (loadedInChromeExtension) {
        APIService.changeAppVersion('chrome', '2.0.0');
      }
      const startNoteProcessingSessionResponse = await startNoteProcessingSession(
        currentTranscriptionSessionId,
        noteInformation,
        accessToken,
      );

      console.log('Note Processing Session Started.');

      // show success dialog
      enqueueSnackbar('Your note has been successfully submitted!', {
        variant: 'success',
      });

      // Clean up recording starter ID
      localStorage.removeItem('recording_starter_id');

      await cleanUpSession(); // call cleanup

      return {
        note_id: startNoteProcessingSessionResponse.note_id,
        thread_id: startNoteProcessingSessionResponse.thread_id,
      };
    } catch (response: any) {
      let snackBarText =
        'Your recording could not be submitted at this time, please try a different template or contact support.';

      if (response.error_code === 'INVALID_TEMPLATE') {
        snackBarText =
          'The template you selected does not contain any sections. Please add a section to it or try a different template.';
      }

      console.error('Could not start Note Processing Session');
      SentryService.logEvent('Error submitting recording.', {
        level: 'error',
        extra: {
          error: response.error,
          transcriptionSessionId: currentTranscriptionSessionId,
        },
      });

      enqueueSnackbar(snackBarText, { variant: 'error' });

      // return recording status state
      setRecordingStatus(prevStatus => ({
        ...prevStatus,
        readyToSubmit: true,
      }));
      return undefined;
    }
  };

  const attemptRecoverRecordingSession = async () => {
    console.log('Attempting to recover a recording session.');
    const recoveredRecordingSessionId = localStorage.getItem('currentRecordingSessionId');
    if (recoveredRecordingSessionId) {
      const recoveredRecordingSession = await getRecordingSessionFromId(
        recoveredRecordingSessionId,
      );
      if (recoveredRecordingSession) {
        console.log(`Successfully recovered recording session ${recoveredRecordingSession.id}`);
        currentRecordingSession.current = recoveredRecordingSession;
      }
    }
  };

  const getRecordingSessionFromId = async (
    recordingSessionId: string,
  ): Promise<RecordingSession | null> => {
    if (recordingSessionId) {
      const recordingSession = await RecordingSession.loadFromStorage(recordingSessionId);
      if (recordingSession) {
        return recordingSession;
      }
    }

    return null;
  };

  // handle reload case
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Only the recording owner should show the "Leave site?" dialog.
      // Non-owner tabs close/reload silently — no dialog, no state mutation.
      if (recordingStatus.isRecording && allowRecorderControl) {
        // Do NOT pause the recording or mutate state.
        // If user stays: recording continues seamlessly.
        // If user leaves: Web Lock auto-releases, SessionRecoveryService handles recovery.
        event.preventDefault();
      }

      // Track session abandonment if mid-completion (draining or offline_pending)
      if (sessionCompletionServiceRef.current) {
        const completionState = sessionCompletionServiceRef.current.getSnapshot();
        if (completionState.status === 'draining' || completionState.status === 'offline_pending') {
          const client = getStatsigClientInstance();
          if (client) {
            client.logEvent(EventNames.SESSION_ABANDONED, undefined, {
              session_id: transcriptionSessionIdRef.current || '',
              completion_status: completionState.status,
              drain_elapsed_seconds: String(completionState.drainElapsedSeconds),
              total_chunks: String(completionState.totalChunks),
              uploaded_chunks: String(completionState.uploadedChunks),
              failed_chunks: String(completionState.failedChunks),
            });
            // Best-effort flush — may not complete before page unloads
            client.flush?.();
          }
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [recordingStatus.isRecording, allowRecorderControl]);

  // Track page unload during active drain — fires synchronously so Statsig SDK
  // picks it up in its own sendBeacon flush on visibilitychange/pagehide.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') return;

      const completionService = sessionCompletionServiceRef.current;
      if (!completionService) return;

      const snapshot = completionService.getSnapshot();
      if (snapshot.status !== 'draining' && snapshot.status !== 'offline_pending') return;

      const uploadSnapshot = uploadServiceRef.current?.getSnapshot();

      sessionCompletionTracking.pageUnload({
        session_id: transcriptionSessionIdRef.current || undefined,
        completion_status: snapshot.status,
        drain_elapsed_seconds: snapshot.drainElapsedSeconds,
        total_chunks: snapshot.totalChunks,
        uploaded_chunks: snapshot.uploadedChunks,
        failed_chunks: snapshot.failedChunks,
        pending_queue_depth: uploadSnapshot?.queueDepth,
        in_flight_count: uploadSnapshot?.inFlightCount,
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [sessionCompletionTracking]);

  // Initialize elapsed time and currentRecordingSession from localStorage on component mount
  useEffect(() => {
    const storedElapsedTime = localStorage.getItem('elapsedTime');
    if (storedElapsedTime) {
      elapsedTime.current = parseInt(storedElapsedTime, 10);
      setElapsedSeconds(elapsedTime.current);
    }

    attemptRecoverRecordingSession();
  }, []);

  // Update localStorage when elapsed time changes.
  // Keyed to elapsedSeconds (state) — the elapsedTime ref never changes
  // identity, so with the ref as a dep this effect only ran on
  // isRecording transitions, never per-second during a recording.
  useEffect(() => {
    if (recordingStatus.isRecording) {
      const minutes = Math.floor(elapsedSeconds / 60);
      const seconds = elapsedSeconds % 60;
      const formattedTime = `${minutes}:${String(seconds).padStart(2, '0')}`;
      sendMessageToExtension({ type: 'recordingTime', data: formattedTime });
      localStorage.setItem('elapsedTime', elapsedSeconds.toString());
      if (elapsedSeconds >= MAX_RECORDING_LENGTH_SECONDS) {
        SentryService.logEvent('Max recording length exceeded.', {
          level: 'info',
          extra: {
            elapsedTime: elapsedSeconds,
            transcriptionSessionId: currentTranscriptionSessionId,
          },
        });
        initiateEndRecording();
      }
    }
  }, [elapsedSeconds, recordingStatus.isRecording]);

  // Release mic-test resources on unmount.
  useEffect(() => {
    return () => {
      micTestLevelMeterRef.current?.stop();
      micTestStreamRef.current?.getTracks().forEach(track => track.stop());
      micTestLevelMeterRef.current = null;
      micTestStreamRef.current = null;
    };
  }, []);

  // Set default template
  useEffect(() => {
    if (templatesList && templatesList.length > 0 && !noteInformation.templateId) {
      setNoteInformation(prev => ({
        ...prev,
        templateId: templatesList[0].template_id,
      }));
    }
  }, [templatesList]);

  // Manage timer based on recording status
  useEffect(() => {
    if (recordingStatus.isRecording && !recordingStatus.recordingPaused) {
      startTimer();
    } else {
      stopTimer();
    }

    // Cleanup timer on component unmount
    return () => {
      stopTimer();
    };
  }, [recordingStatus.isRecording, recordingStatus.recordingPaused]);

  useEffect(() => {
    console.log('Updating loadedInChromeExtension', loadedInChromeExtension);
    isLoadedInChromeExtension.current = loadedInChromeExtension;
  }, [loadedInChromeExtension]);

  // Get accurate recording times based on actual start time
  const getRecordingTimes = () => {
    if (!recordingStartTime.current) {
      return null;
    }

    return {
      startTime: new Date(recordingStartTime.current),
      endTime: new Date(), // Use actual current time, not calculated
      elapsedTime: elapsedTime.current,
    };
  };

  // Update checkRecordingOwnership to track previous value of allowRecorderControl
  const prevAllowRecorderControl = useRef(allowRecorderControl);

  // Define checkRecordingOwnership before any usage
  const checkRecordingOwnership = useCallback(() => {
    // Get the current recording starter ID
    const currentStarterId = localStorage.getItem('recording_starter_id');

    if (currentStarterId) {
      const currentTabId = tabCommunicator.getTabId();
      const isRecordingStarter = currentStarterId === currentTabId;

      // Only update if the value is changing
      if (isRecordingStarter !== prevAllowRecorderControl.current) {
        setAllowRecorderControl(isRecordingStarter);
        prevAllowRecorderControl.current = isRecordingStarter;
      }
    } else {
      // If there's no active session, allow this tab to control the recording
      if (!prevAllowRecorderControl.current) {
        setAllowRecorderControl(true);
        prevAllowRecorderControl.current = true;
      }
    }
  }, []);

  // Update the visibility change listener
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Tab became visible, checking recording status');
        checkRecordingOwnership();
      }
    };

    // Add event listener for visibility change
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initial check when component mounts
    checkRecordingOwnership();

    // Clean up event listener on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkRecordingOwnership]);

  // Visibility-aware stream suspension: release mic stream when tab goes to background
  // (avoids holding a single-consumer resource in inactive tabs), re-acquire when visible.
  // Never interrupts an active recording.
  useEffect(() => {
    if (!audioInputActive) return;

    const handleStreamVisibility = () => {
      const service = audioDeviceServiceRef.current;
      if (!service?.isInitialized()) return;

      if (document.visibilityState === 'hidden') {
        // Tab hidden — release stream if not recording
        if (!isRecordingRef.current && service.getActiveStream()) {
          service.release();
          setAudioHealthState('idle');
        }
      } else if (document.visibilityState === 'visible') {
        // Tab visible — re-acquire if stream was suspended
        if (!isRecordingRef.current && !service.getActiveStream()) {
          const savedMicId = localStorage.getItem('selectedMicrophoneId');
          const devices = service.getDevices();
          const target = savedMicId
            ? devices.find(d => d.browserDeviceId === savedMicId)
            : undefined;

          service.acquire(target?.internalId).catch(error => {
            console.warn('Stream re-acquire on tab focus failed (non-fatal)', error);
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleStreamVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleStreamVisibility);
    };
  }, [audioInputActive]);

  // Hook to monitor localStorage changes from other tabs
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      // Handle recording_starter_id changes
      if (event.key === 'recording_starter_id') {
        const oldStarterId = event.oldValue;
        const newStarterId = event.newValue;
        const currentTabId = tabCommunicator.getTabId();

        // If this tab lost ownership, stop the permanent recording pipeline.
        if (oldStarterId === currentTabId && newStarterId !== currentTabId) {
          console.log('Stopping recording because tab changed');
          if (recordingServiceRef.current) {
            recordingServiceRef.current
              .stop()
              .then(() => {
                // Clean up mixer/stream resources after stop completes
                streamCleanupRef.current?.();
                streamCleanupRef.current = null;
                mixerResultRef.current = null;
              })
              .catch(err => {
                console.warn('Error stopping RecordingService on tab ownership loss:', err);
              });
          }
        }

        // Check ownership when recording_starter_id changes
        checkRecordingOwnership();
      }

      // Handle recordingStatus changes from other tabs
      // This is the simple cross-tab sync - when Tab 1 updates localStorage, Tab 2 gets notified
      if (event.key === 'recordingStatus' && event.newValue) {
        try {
          const newStatus = JSON.parse(event.newValue);
          console.debug('Recording status updated from another tab:', newStatus);
          setRecordingStatus(newStatus);

          // If recording stopped and we're not the owner, clear stale recording_starter_id
          if (!newStatus.isRecording) {
            const currentStarterId = localStorage.getItem('recording_starter_id');
            const currentTabId = tabCommunicator.getTabId();
            if (currentStarterId && currentStarterId !== currentTabId) {
              console.debug('Clearing stale recording_starter_id after remote stop');
              localStorage.removeItem('recording_starter_id');
            }
            // Re-check ownership now that recording has stopped
            checkRecordingOwnership();
          }
        } catch (e) {
          console.error('Error parsing recordingStatus from storage event', e);
        }
      }
    };

    // Add event listener for storage changes
    window.addEventListener('storage', handleStorageChange);

    // Clean up event listener on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [checkRecordingOwnership, setRecordingStatus]);

  // Add effect to call rehydrateStateFromLocalStorage when allowRecorderControl becomes true
  useEffect(() => {
    if (allowRecorderControl) {
      console.log('allowRecorderControl became true, rehydrating state');
      rehydrateStateFromLocalStorage();
    } else if (!allowRecorderControl) {
      console.log('Lost recording control, stopping recording pipeline');
      if (recordingServiceRef.current) {
        recordingServiceRef.current.stop().catch(err => {
          console.warn('Error stopping RecordingService on control loss:', err);
        });
      }
    }
  }, [allowRecorderControl, rehydrateStateFromLocalStorage]);

  // Update takeOverRecording to use the new check function
  const takeOverRecording = async (): Promise<boolean> => {
    try {
      console.log('Taking over recording to this tab');

      SentryService.logEvent('RecordingTabTakeover', {
        level: 'warning',
        extra: {
          currentTabId: tabCommunicator.getTabId(),
          previousTabId: localStorage.getItem('recording_starter_id'),
          recordingStatus: recordingStatus,
          allowRecorderControl: allowRecorderControl,
        },
      });

      // Set recording_starter_id in localStorage first to claim ownership
      localStorage.setItem('recording_starter_id', tabCommunicator.getTabId());

      // First set the local state - do this immediately
      setAllowRecorderControl(true);
      prevAllowRecorderControl.current = true;

      // Log the current state after taking over
      console.log('Recording ownership claimed by tab:', tabCommunicator.getTabId());
      console.log('Current recording status:', recordingStatus);

      // Force rehydration of state from localStorage
      rehydrateStateFromLocalStorage();

      // If recording is paused after a reload, resume through RecordingService.
      if (recordingStatus.recordingPaused) {
        if (recordingServiceRef.current) {
          const serviceStatus = recordingServiceRef.current.getSnapshot().status;
          if (serviceStatus !== 'idle' && serviceStatus !== 'paused') {
            // RecordingService already has an active recorder — nothing to do
          } else {
            try {
              // Acquire stream
              let stream: MediaStream;
              const activeStream = audioDeviceServiceRef.current?.getActiveStream();
              if (activeStream) {
                stream = activeStream.stream;
              } else {
                const deviceId = selectedMicrophone?.deviceId;
                stream = await navigator.mediaDevices.getUserMedia({
                  audio: deviceId ? { deviceId: { exact: deviceId } } : true,
                });
              }

              // Determine next chunk index from storage
              const sessionId =
                transcriptionSessionIdRef.current ||
                localStorage.getItem('current_transcription_session_id') ||
                '';
              const storageService = storageServiceRef.current;
              if (!storageService) {
                throw new Error('StorageService unavailable during recording recovery');
              }
              const chunks = await storageService.listChunks(sessionId);
              const positiveChunks = chunks.filter(i => i >= 0);
              const nextChunkIndex =
                positiveChunks.length > 0 ? Math.max(...positiveChunks) + 1 : 0;

              await recordingServiceRef.current.resumeFromColdStart(
                stream,
                sessionId,
                nextChunkIndex,
              );
            } catch (error) {
              console.error('Failed to cold-resume via RecordingService:', error);
              SentryService.captureException(error, {
                level: 'error',
                tags: { component: 'RecordingContext', method: 'takeOverRecording' },
              });
              return false;
            }
          }
        } else {
          console.error('RecordingService unavailable during recording takeover');
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('Error taking over recording:', error);
      SentryService.logEvent('Error taking over recording.', {
        level: 'error',
        extra: { error },
      });
      return false;
    }
  };

  // Request remote stop via BroadcastChannel
  // Check ownership at runtime using localStorage to avoid stale closure issues
  const requestRemoteStop = useCallback(async (): Promise<{
    success: boolean;
    reason?: string;
  }> => {
    // Check ownership at runtime by reading directly from localStorage
    const currentStarterId = localStorage.getItem('recording_starter_id');
    const currentTabId = tabCommunicator.getTabId();
    const isOwner = !currentStarterId || currentStarterId === currentTabId;

    // If this tab has control, stop directly using the ref to avoid stale closure
    if (isOwner) {
      const result = await performStopRecordingRef.current();
      return { success: result };
    }

    // Otherwise, request remote stop via BroadcastChannel
    setRemoteStopPending(true);
    try {
      const result = await tabCommunicator.broadcastStopRequest();
      return result;
    } finally {
      setRemoteStopPending(false);
    }
  }, []);

  const requestRemotePause = useCallback(async (): Promise<{
    success: boolean;
    reason?: string;
  }> => {
    // Check ownership at runtime by reading directly from localStorage
    const currentStarterId = localStorage.getItem('recording_starter_id');
    const currentTabId = tabCommunicator.getTabId();
    const isOwner = !currentStarterId || currentStarterId === currentTabId;

    // If this tab has control, pause directly using the ref to avoid stale closure
    if (isOwner) {
      const result = performPauseRecordingRef.current();
      return { success: result };
    }

    // Otherwise, request remote pause via BroadcastChannel
    try {
      const result = await tabCommunicator.broadcastPauseRequest();
      return result;
    } catch (error) {
      return { success: false, reason: error instanceof Error ? error.message : String(error) };
    }
  }, []);

  const requestRemoteResume = useCallback(async (): Promise<{
    success: boolean;
    reason?: string;
  }> => {
    // Check ownership at runtime by reading directly from localStorage
    const currentStarterId = localStorage.getItem('recording_starter_id');
    const currentTabId = tabCommunicator.getTabId();
    const isOwner = !currentStarterId || currentStarterId === currentTabId;

    // If this tab has control, resume directly using the ref to avoid stale closure
    if (isOwner) {
      const result = await performResumeRecordingRef.current();
      return { success: result };
    }

    // Otherwise, request remote resume via BroadcastChannel
    try {
      const result = await tabCommunicator.broadcastResumeRequest();
      return result;
    } catch (error) {
      return { success: false, reason: error instanceof Error ? error.message : String(error) };
    }
  }, []);

  // Memoize the context value
  const value = useMemo(
    () => ({
      currentTranscriptionSessionId,
      recordingStatus,
      noteInformation,
      setNoteInformation,
      setUseSharedMedia,
      useSharedMedia,
      noAudioStatus,
      selectedMicrophone,
      availableMicrophones,
      microphonePermissionGranted,
      checkMicrophonePermission,
      setSelectedMicrophone,
      setNoAudioStatus,
      getAvailableMicrophones,
      initiateRecording,
      initiateEndRecording,
      sendRecording,
      pauseRecording,
      resumeRecording,
      deleteRecordingHelper,
      startTimer,
      stopTimer,
      resetTimer,
      elapsedTime: elapsedSeconds,
      appointmentType,
      setAppointmentType,
      cleanUpSession,
      softResetForNavigation,
      releaseMicrophone,
      lowDataMode,
      sendMessageToExtension,
      setShouldBeCleanedUp,
      takeOverRecording,
      allowRecorderControl,
      rehydrateStateFromLocalStorage,
      checkRecordingOwnership,
      getRecordingTimes,
      startMicTest,
      stopMicTest,
      isMicTesting,
      onS3UploadError,
      setOnS3UploadError,
      setOnRecordingFinalized,
      setOnRecordingStopped,
      setBeforeRecordingStart,
      beforeStartGatePending,
      requestRemoteStop,
      requestRemotePause,
      requestRemoteResume,
      remoteStopPending,
      audioHealthState,
      releaseAudioStream,
      needsMicPermission,
      setAudioInputActive,
      setRecordingEncounterId,
      getRecordingEncounterId,
      setTelehealthSession,
      cleanupTelehealthSession,
      audioInputDeviceService: exposedAudioDeviceService,
      resetRecordingState,
      pipelineError,
      retryPipelineInit,
      sessionCompletionService: exposedCompletionService,
      sessionRecoveryService: exposedRecoveryService,
      uploadService: exposedUploadService,
      recordingService: exposedRecordingService,
      storageService: exposedStorageService,
    }),
    [
      recordingStatus,
      noteInformation,
      noAudioStatus,
      selectedMicrophone,
      availableMicrophones,
      microphonePermissionGranted,
      elapsedSeconds,
      appointmentType,
      useSharedMedia,
      lowDataMode,
      allowRecorderControl,
      rehydrateStateFromLocalStorage,
      isMicTesting,
      onS3UploadError,
      setOnS3UploadError,
      setOnRecordingFinalized,
      setOnRecordingStopped,
      setBeforeRecordingStart,
      beforeStartGatePending,
      requestRemoteStop,
      requestRemotePause,
      requestRemoteResume,
      remoteStopPending,
      audioHealthState,
      needsMicPermission,
      resetRecordingState,
      pipelineError,
      retryPipelineInit,
      exposedCompletionService,
      exposedRecoveryService,
      exposedUploadService,
      exposedRecordingService,
      exposedStorageService,
      exposedAudioDeviceService,
    ],
  );

  // Provide the context to children components
  return (
    <RecordingContext.Provider value={value}>
      {children}
      {exposedStorageService && (
        <StorageCleanupDialog
          open={showStorageCleanupDialog}
          onClose={() => setShowStorageCleanupDialog(false)}
          onCleanupComplete={() => {
            // Check storage again after cleaning up
            initiateRecording().catch(error => {
              console.error('Error starting recording after cleanup:', error);
            });
          }}
          storageInfo={
            storageInfo
              ? {
                  usagePercent: storageInfo.usagePercent,
                  availableMB: storageInfo.availableMB,
                  quotaMB: storageInfo.quotaMB,
                }
              : undefined
          }
          minimumRequiredQuotaMB={storageInfo?.minimumRequiredQuotaMB}
          storageService={exposedStorageService}
        />
      )}
    </RecordingContext.Provider>
  );
};
